export default {
    "sidebar": {
        "globalTemplates": "Global templates",
        "profileSaveError": "Error saving the profile: {{msg}}",
        "mfaDisableError": "MFA could not be disabled: {{msg}}",
        "title": "CAF Suite",
        "dashboard": "Assessment Overview",
        "learningHub": "Learning Hub",
        "assessments": "Assessments",
        "prioritization": "Prioritization",
        "admin": "Admin",
        "contentManagement": "Content Management",
        "user": "User Management",
        "settings": "Settings",
        "superAdmin": "Tenant Management",
        "logout": "Logout",
        "editProfile": "Edit Profile",
        "menu": "Menu",
        "projectManagement": "Project Management",
        "assessmentSection": "Assessment",
        "security": "Security",
        "mfaStatus": {
            "active": "Active",
            "activeEnforced": "Active (Enforced)",
            "notSetup": "Not Set Up",
            "disable": "Disable",
            "setup": "Set Up"
        },
        "myTasks": "My Tasks",
        "online": "Online",
        "offline": "Offline",
        "eMail": "E-Mail",
        "benachrichtigungen": "Notifications",
        "kommentareErwaehnungen": "Comments & Mentions",
        "sofortBenachrichtigen": "Notify Immediately",
        "taeglicheZusammenfassung": "Daily Summary",
        "keineEMails": "No E-Mails",
        "erinnerungen": "Reminders",
        "faelligeAufgabenMassnahmen": "Due Tasks & Measures",
        "erhaltenSieEMailsWenn": "Receive emails when a reminder date is reached.",
        "ihrName": "Your Name",
        "mfaDeaktivieren": "Disable MFA?",
        "mfaDeaktiviert": "MFA Disabled",
        "portfolio": "Project Portfolio",
        "languageSettings": "Language",
        "twoFactorAuth": "Two-Factor Authentication",
        "mfaDisableConfirmMessage": "Do you really want to disable two-factor authentication? Your account will be less protected.",
        "mfaDisableConfirmButton": "Disable",
        "mfaDisableSuccessMessage": "Two-factor authentication has been successfully disabled.",
        "assessment": "Assessments",
        "tasks": "Tasks",
        "userManagement": "User Management"
    },
    "header": {
        "dashboard": "Assessment Overview",
        "learning": "Learning Hub",
        "assessment": "Assessments",
        "prioritization": "Prioritization of Potentials",
        "contentManagement": "Content Management"
    },
    "login": {
        "navGesamtsicht": "Overview",
        "needToKnowTitle": "Need-to-know between units",
        "needToKnowDesc": "Each organizational unit sees only its own data and what others have explicitly shared with it. The server enforces the access rules, and they are tested automatically before every release.",
        "heroChips": {
            "levels": "Overview across all levels",
            "topics": "Cross-cutting topics under control",
            "evidence": "Decisions on solid ground"
        },
        "levels": {
            "title": "One platform for all levels",
            "lead": "Projects and assessments originate where the work is done. The CAF Suite takes them to where decisions are made – without manual data collection and status slides.",
            "aggregateTitle": "Consolidate assessments",
            "aggregateDesc": "Each unit assesses itself and decides when to release its result to its parent unit. The parent unit sees the average of its subordinate units, year by year. Comparisons only appear from three units upwards – so self-assessments stay honest.",
            "promoteTitle": "Pass projects upwards",
            "promoteDesc": "Whatever matters beyond its own unit is passed on by portfolio management one level at a time: from the office to the directorate, from the directorate to the canton. The originating unit stays responsible, and each level prioritizes by its own criteria. Who passed on what and when remains traceable.",
            "topicsTitle": "Steer cross-cutting topics",
            "topicsDesc": "IT, sustainability, geodata: with a cross-cutting topic, the responsible specialist unit sees all affected projects, whichever directorate they come from. Duplication becomes visible early, and capacities can be planned. Before saving, the CAF Suite shows who will gain insight.",
            "needToKnowLabel": "Need-to-know:",
            "needToKnowDesc": "Each unit owns its data. Others see only what has been explicitly shared – enforced on the server, not just in the interface.",
            "growLabel": "Start small, grow later:",
            "growDesc": "A directorate can start on its own and later become part of the whole organization without losing any data."
        },
        "emailPlaceholder": "name@organization.ch",
        "passwordRequired": "Password is required",
        "linkingWrongPassword": "Incorrect password. Please try again.",
        "linkingTooManyRequests": "Too many attempts. Your account has been temporarily locked. Please reset your password.",
        "linkingFailed": "Account linking failed.",
        "mfaNoSupportedFactor": "No supported second factor found.",
        "linkingAccountExists": "You are trying to log in with SSO for the first time, but your email address <strong>{{email}}</strong> is already registered with a password account.",
        "linkingEnterOldPassword": "Please enter <strong>your old password one last time</strong> to securely link the two login methods. After that, you can conveniently log in with SSO.",
        "authErrors": {
            "invalidCredentials": "Sign-in failed. Please check your email address and password.",
            "tooManyRequests": "Too many attempts. The account has been temporarily locked. Please try again later.",
            "network": "Connection error. Please check your internet connection.",
            "unexpected": "An unexpected error occurred.",
            "invalidEmail": "Invalid email format.",
            "resetFailed": "The email could not be sent. Please try again later."
        },
        "oeffentlicheVerwaltung": "public administration:",
        "introText": "The CAF Suite connects the CAF self-assessment with the steering of your projects – from the office through the directorate up to the whole organization. Each unit works independently. The level above sees consolidated results and the projects that matter to it. Specialist units such as IT keep track of their cross-cutting topics across all directorates.",
        "managementIntro": "In the intensive day-to-day business of public administration, it is often difficult to keep the strategic focus. The CAF Suite gives management the security to professionally develop the organization. You can logically demonstrate at any time why which priorities were set and what the current implementation status looks like.",
        "managementQuote": "«Better to focus on a few, correct initiatives – and then successfully bring them to the streets.»",
        "pillar1Desc": "Use the proven European CAF model for structured self- and external assessments. Instead of relying on subjective assessments, you rely on reliable data to precisely identify the strengths and improvement potentials of your organization – in every unit and consolidated for the level above.",
        "pillar2Desc": "Not everything can be done at the same time. Our prioritization tool helps you and your team to sort identified potentials by relevance and urgency. In this way, you make well-founded resource and budget decisions and focus exactly where it achieves the greatest impact.",
        "pillar3Title": "Realize strategies: Portfolio & Project Management",
        "pillar3Desc": "Transform potentials into measurable success. The integrated project portfolio shows every level the initiatives it has to steer – from its own unit up to the whole organization. Manage projects with detailed roadmaps, matrix views, and pulse checks (traffic lights for schedule, costs, resources and quality). The integrated task and board management ensures that the team handles tasks in a controlled way and meets deadlines.",
        "pillar4Title": "Build know-how: The Learning Hub & Method Kit",
        "pillar4Desc": "Empower your employees: The integrated Learning Hub provides quick access to proven methods for every single CAF criterion. Whether 360-degree feedback, agile leadership circles, or Balanced Scorecard – the method kit provides the necessary expertise exactly where it is needed.",
        "sicherheitUndDatenschutz": "Security & Privacy",
        "securityIntro": "Highest standards for authorities. Our infrastructure is designed for protection, sovereignty, and compliance – so you can focus entirely on organizational development.",
        "iacAudit": "Infrastructure as Code (IaC) – completely auditable",
        "isdsDesc": "We provide a comprehensive ISMS concept template with all essential contents – this significantly relieves your project management during onboarding.",
        "kontenVerknuepfenBtn": "Link Accounts",
        "sso": {
            "lookupAppCheck": "Your browser's security check has not finished yet, so sign-in through your organization (SSO) could not be checked. Please try again in a few seconds or reload the page.",
            "lookupFailed": "Sign-in through your organization (SSO) could not be checked. Please try again.",
            "linkPrepareFailed": "The account already exists, but linking could not be prepared.",
            "popupClosed": "The sign-in window was closed.",
            "popupBlocked": "The browser blocked the sign-in window. Please allow pop-ups for this site.",
            "orgLoginButton": "Login with Organization (SSO)",
            "domainDetected": "Your organization ({{tenantName}}) uses Single Sign-On.",
            "domainDetectedHint": "Click 'Login' to log in via your organization account.",
            "ssoError": "SSO Login failed. Please try again.",
            "ssoLoading": "Starting SSO...",
            "checkingDomain": "Checking Organization...",
            "fallbackHint": "Alternatively login with Email and Password",
            "providerNotFound": "SSO Provider not configured."
        },
        "passwortZuruecksetzen": "Reset Password",
        "willkommenZurueck": "Welcome Back",
        "wirSendenIhnenEinenLink": "We will send you a link via email.",
        "bitteMeldenSieSichAn": "Please log in with your authority account.",
        "verarbeite": "Processing...",
        "linkAnfordern": "Request Link",
        "anmelden": "Sign In",
        "funktionen": "Features",
        "sicherheit": "Security",
        "kontakt": "Contact",
        "kontinuierlicheOrganisationsentwicklung": "Continuous Organizational Development",
        "exzellenzFuerDie": "Excellence for the",
        "heroSubline": "From insight to implementation – across all levels.",
        "eMailVersendet": "E-Mail Sent!",
        "bittePruefenSieIhrenPosteingang": "Please check your inbox for further instructions.",
        "zurueckZumLogin": "Back to Login",
        "eMailAdresse": "Email Address",
        "passwort": "Password",
        "passwortVergessen": "Forgot Password?",
        "abbrechen": "Cancel",
        "tlsConnection": "Encrypted connection (TLS)",
        "souveraenFuehrenFundiertEntscheidenSicher": "Lead confidently. Decide factually. Implement securely.",
        "die4SaeulenDerOrganisatorischen": "The 4 Pillars of Organizational Development",
        "evidenzStattBauchgefuehlDieDigitale": "Evidence instead of gut feeling: The digital assessment",
        "fokusAufDasWesentlicheIntelligente": "Focus on the essentials: Smart prioritization",
        "dieCafSuiteImEinsatz": "The CAF Suite in action",
        "dataLocationTitle": "Business data in Switzerland",
        "dataLocationDesc": "Assessments, projects and measures are stored and processed in ISO-certified Google Cloud data centers in Zurich. The optional AI features use data centers in the EU.",
        "encryptionTitle": "Encrypted in transit and at rest",
        "encryptionDesc": "TLS encryption in transit and AES-256 encryption of stored data (data at rest).",
        "dataProtectionTitle": "Data protection under the FADP and GDPR",
        "dataProtectionDesc": "Designed for the Swiss Federal Act on Data Protection (FADP) and the EU General Data Protection Regulation (GDPR). We offer data processing agreements (DPA) for authorities.",
        "technischeStandards": "Technical Standards",
        "webApplicationFirewallWafMit": "Web Application Firewall (WAF) with Cloud Armor",
        "wcagCompliance": "Accessibility compliant with WCAG 2.1 AA",
        "ciCdPipelineMitAutomatisierten": "CI/CD Pipeline with automated security checks",
        "singleSignOnSsoVia": "Single Sign-On (SSO) via SAML 2.0 / OIDC / Microsoft Entra ID",
        "zweiFaktorAuthentifizierungTotp": "Two-Factor Authentication (TOTP)",
        "taeglicheAutomatisierteBackups": "Daily Automated Backups",
        "auditLoggingRelevanterAenderungen": "Audit logging of relevant changes",
        "isdsKonzept": "ISMS Concept:",
        "habenSieFragenOderWuenschen": "Do you have questions or want a demo?",
        "contactTeaserText": "We are happy to show you the CAF Suite with a sample organization across three levels – from the office to the canton.",
        "kontaktAufnehmen": "Contact Us",
        "impressum": "Imprint",
        "datenschutz": "Privacy Policy",
        "hilfe": "Help",
        "privateDigital": "PRIVATE DIGITAL",
        "johannesSpring": "Johannes Spring",
        "voiaCumpogna1": "Voia Cumpogna 1",
        "schweiz": "Switzerland",
        "eMail": "E-Mail:",
        "haftungshinweisTrotzSorgfaeltigerInhaltlicherKontrolle": "Disclaimer: Despite careful content control, we assume no liability for the content of external links. The operators of the linked pages are solely responsible for their content.",
        "wieErstelleIchEinenNeuen": "How do I create a new user?",
        "benutzerKoennenNurVonAdministratoren": "Users can only be invited by administrators of your organization. Please contact your IT department or the CAF project manager of your authority.",
        "technischerSupport": "Technical Support",
        "beiTechnischenProblemenOderFehlermeldungen": "We are happy to assist you with technical problems or error messages.",
        "supportKontaktieren": "Contact Support",
        "kontoVerknuepfen": "Link Account",
        "ihreEMailAdresseExistiert": "Your email address already exists.",
        "sieVersuchenSichDasErste": "You are trying to log in with SSO for the first time, but your email address",
        "bitteGebenSie": "Please enter",
        "bisherigesPasswort": "Current Password",
        "verknuepfe": "Linking...",
        "datenschutzerklaerung": "Data Privacy Statement",
        "hilfeSupport": "Help & Support",
        "alleRechteVorbehalten": "© {{year}} PRIVATE DIGITAL Johannes Spring. All rights reserved.",
        "hilfePasswortText": "Click on \"Forgot password\" on the login page to receive a link to reset your password by email.",
        "carousel": {
            "portfolioSharing": "Passing a project on across levels",
            "topicRoadmap": "Road map of a cross-cutting topic",
            "assessmentAggregate": "Aggregate of the subordinate units",
            "portfolioChart": "Project Portfolio – Matrix View",
            "portfolioTimeline": "Project Portfolio – Road Map",
            "selectProject": "Project Selection",
            "projectDetails": "Project Details",
            "tasks": "Task Board (Kanban)",
            "myTasks": "My Tasks",
            "assessmentOverview": "Assessment Overview",
            "assistent": "Assessment Assistant",
            "criteria": "Model Information",
            "methods": "Methods Toolbox"
        }
    },
    "finishassessorregistration": {
        "keinGueltigerLink": "No valid link found.",
        "einladungslinkUngueltig": "The invitation link has expired or is invalid.",
        "speichere": "Saving...",
        "registrierungAbschliessenBtn": "Complete Registration",
        "linkUngueltig": "Invalid Link",
        "zurueckZumLogin": "Back to Login",
        "registrierungAbschliessen": "Complete Registration",
        "vervollstaendigenSieIhrProfilFuer": "Complete your profile for ",
        "ihrName": "Your Name",
        "firmaOrganisation": "Company / Organization",
        "passwort": "Password",
        "wiederholen": "Repeat",
        "vornameNachname": "First Name, Last Name",
        "consultingGmbh": "Consulting GmbH",
        "esIstEinFehlerAufgetreten": "An error occurred. Please try again.",
        "erfolgreich": "Success!",
        "abschlussFehlgeschlagen": "Completion failed.",
        "dieRegistrierungWarErfolgreichSie": "Registration successful. You will be redirected shortly..."
    },
    "setpassword": {
        "passwortZuruecksetzen": "Reset Password",
        "resetText": "Set a new password. You can then sign in with your email address and the new password.",
        "neuesPasswortFestlegen": "Set New Password",
        "linkUngueltig": "Invalid Link",
        "passwoerterStimmenNichtUeberein": "The passwords do not match",
        "linkUngueltigOderAbgelaufen": "The link is invalid or has expired.",
        "linkAbgelaufen": "The link has expired.",
        "linkAbgelaufenOderVerwendet": "The link has expired or has already been used.",
        "passwortZuSchwach": "The password is too weak.",
        "fehlerAufgetreten": "An error occurred. Please try again.",
        "supportHinweis": "If you run into technical problems or error messages, please send an email with a screenshot and a description to support@caf-suite.ch.",
        "fuer": "for {{email}}",
        "gebenSieIhrNeuesPasswortEin": "Enter your new password.",
        "speichern": "Saving...",
        "passwortSpeichern": "Save Password",
        "einladungAnnehmen": "Accept Invitation",
        "accountAktivierung": "Account Activation",
        "willkommenBeiDer": "Welcome to",
        "cafSuite": "CAF Suite.",
        "bitteLegenSieEinSicheres": "Please set a secure password to activate your account and access your organization.",
        "passwortFestlegen": "Set Password",
        "erfolgreich": "Success!",
        "ihrPasswortWurdeGespeichertSie": "Your password has been saved. You will be redirected shortly...",
        "neuesPasswort": "New Password",
        "passwortBestaetigen": "Confirm Password",
        "sichereSslVerschluesselung": "Secure SSL Encryption",
        "impressum": "Imprint",
        "datenschutz": "Privacy Policy",
        "hilfe": "Help",
        "privateDigital": "PRIVATE DIGITAL",
        "johannesSpring": "Johannes Spring",
        "voiaCumpogna1": "Voia Cumpogna 1",
        "schweiz": "Switzerland",
        "eMail": "E-Mail:",
        "haftungshinweisTrotzSorgfaeltigerInhaltlicherKontrolle": "Disclaimer: Despite careful content control, we assume no liability for the content of external links. The operators of the linked pages are solely responsible for their content.",
        "datenschutzerklaerung": "Data Privacy Statement",
        "hilfeSupport": "Help & Support"
    },
    "contact": {
        "companyRequired": "Organization is required for quotes and demos",
        "title": "Contact us",
        "topic": "Topic",
        "topics": {
            "general": "General Inquiry",
            "quote": "Request a Quote",
            "demo": "Request a Demo",
            "support": "Support"
        },
        "name": "Name",
        "email": "E-Mail",
        "organization": "Organization",
        "phone": "Phone (optional)",
        "subject": "Subject",
        "subjectPlaceholders": {
            "quote": "Quote request for...",
            "demo": "Demo request for...",
            "support": "Problem with...",
            "general": ""
        },
        "message": "Message",
        "send": "Send Message",
        "sending": "Sending...",
        "successTitle": "Thank you!",
        "successMessage": "We have received your message and will get back to you as soon as possible.",
        "close": "Close",
        "backendError": "Backend unavailable",
        "genericError": "An error occurred. Please try again later."
    },
    "privacycontent": {
        "allgemeines": "1. General",
        "derSchutzIhrerDatenHat": "The protection of your data is our highest priority. We process personal data exclusively within the framework of the legal provisions (GDPR, FADP).",
        "datenerhebungBeiNutzung": "2. Data Collection during Use",
        "wennSieUnsereSoftwareNutzen": "When you use our software, we collect the following technically necessary data:",
        "ipAdresse": "IP Address",
        "datumUndUhrzeitDerAnfrage": "Date and Time of the Request",
        "inhaltDerAnforderungKonkreteSeite": "Content of the Request (specific page)",
        "browserUndBetriebssystem": "Browser and Operating System",
        "benutzerkonten": "3. User Accounts",
        "fuerDieNutzungDerApp": "A user account is required to use the app. In doing so, we store your email address, your name (optional), and your affiliation with an organization (tenant).",
        "ihreRechte": "4. Your Rights",
        "ihreRechteText": "You have the right to request information, correction, deletion, and restriction of the processing of your stored data. Please contact our data protection officer at datenschutz@caf-suite.ch.",
        "kiFunktionen": "5. AI features (optional)",
        "kiFunktionenText": "The CAF Suite offers optional AI features, such as project suggestions or a management summary. Your organization can switch them off at any time; no data is then sent to the AI service. When a feature is switched on, the CAF Suite sends only the content it needs (e.g. the scores and remarks of an assessment or the text of a potential) to Google Vertex AI. Names and e-mail addresses of user accounts are not sent. Processing takes place in data centers in the EU, not in Switzerland. Google does not use the data to train its models but may log requests to detect abuse. The CAF Suite stores the responses only if you adopt a suggestion.",
        "hinweisZurCloudNutzung": "6. Note on Cloud Usage & Personal Data",
        "dieDatenWerdenInGoogle": "The data is stored in Google Cloud data centers (Zurich). We would like to point out that when using US cloud providers, theoretical access rights by US authorities (Cloud Act) are discussed.",
        "empfehlungDieseAppDientDer": "Recommendation: This app is intended for strategic organizational analysis (CAF). Please do not enter sensitive personal data of citizens (e.g., in actions or comments). This is not required for the purpose of the application."
    },
    "mfa": {
        "setupTitle": "Set Up Two-Factor Authentication",
        "setupSubtitle": "Strengthen the security of your account",
        "scanStep": "1. Scan Code",
        "appInstruction": "Open your authenticator app (Google Authenticator, Microsoft Authenticator, etc.) and scan this code.",
        "verifyStep": "2. Confirm",
        "enterCode": "Enter the 6-digit code",
        "verifyButton": "Verify",
        "successMessage": "MFA set up successfully!",
        "errorInvalidCode": "Invalid code. Please try again.",
        "setupError": "Error setting up two-factor authentication. Please reload the page.",
        "verificationTitle": "Confirm Two-Factor Authentication",
        "verificationIntro": "Enter the 6-digit code from your authenticator app to confirm your identity.",
        "emailNotVerified": "E-Mail Not Verified",
        "emailNotVerifiedMessage": "Your e-mail address must be verified before you can set up 2FA. Please check your inbox or request a new verification e-mail.",
        "resendVerification": "Resend Verification E-Mail",
        "verificationSent": "E-Mail sent! Please check your inbox.",
        "verifyError": "The code is invalid. Please try again.",
        "mfaNotActivated": "MFA is not yet activated for this project (Identity Platform). Please contact your admin.",
        "emailNotVerifiedShort": "E-Mail not verified.",
        "recentLoginRequired": "Security timeout: Please log in again to set up MFA.",
        "sendEmailError": "Error sending e-mail: ",
        "activated": "2FA Activated!",
        "enforcedNotice": "Your organization requires a second factor. You cannot use CAF Suite without setting it up. You can cancel and sign out; you will continue here the next time you sign in.",
        "cancelAndLogout": "Cancel and sign out",
        "activatedShort": "2FA Activated",
        "verifying": "Verifying..."
    },
    "dashboard": {
        "brushRange": "Period from {{start}} to {{end}}",
        "overallScore": "Overall Score",
        "overallScoreDate": "Overall Score ({{date}})",
        "activeMeasures": "Active Measures",
        "assessmentTrend": "Assessment Overview",
        "compareLast": "Compare last:",
        "assessments": "{{count}} Assessments",
        "breakdown": "Detailed Analysis",
        "breakdownDate": "Detailed Analysis ({{date}})",
        "noAssessmentData": "No assessment data for the selected date.",
        "vs": "vs.",
        "startNewAssessment": "Start New Assessment",
        "notAvailable": "N/A",
        "welcome": {
            "title": "Welcome to your CAF Suite!",
            "message": "This is your dashboard for continuous improvement. Use the self-assessment module to conduct new assessments.",
            "cta": "Go to Assessment Module"
        },
        "kpi": {
            "score": "Current Score",
            "scoreSub": "Average (Total)",
            "potentials": "Number of Potentials",
            "potentialsSub": "From current assessment",
            "addressed": "Implemented Potentials",
            "addressedSub": "Covered by a project"
        },
        "compareWith": "Compare with:",
        "subtitle": "Your cockpit for continuous improvement.",
        "selectOrgScope": "Organizational Unit / Evaluation",
        "scopeOwn": "Own Organization",
        "scopeRollup": "Consolidated View (Rollup)",
        "noSharedAssessmentsTitle": "No Shared Assessments",
        "noSharedAssessmentsMsg": "This organizational unit has not yet shared any self-assessments for higher-tier review.",
        "tabs": {
            "nav": "Tabs",
            "status": "Status Overview",
            "trends": "Long-term Trends"
        },
        "analysis": {
            "title": "Long-term Development",
            "filter": "Analysis Filter",
            "filterSeries": "Filter Data Series",
            "filterCriteria": "Filter Criteria (from active themes)",
            "topImprovers": "Top 3 Improvements",
            "topDecliners": "Top 3 Declines",
            "reset": "Reset",
            "highlights": "Highlights in Period",
            "topDevelopments": "Top Developments",
            "biggestDeclines": "Biggest Declines",
            "table": {
                "criterion": "Criterion",
                "current": "Current",
                "trend": "Trend",
                "compare": "Comparison"
            },
            "chart": {
                "themePrefix": "Th.",
                "total": "Total"
            }
        },
        "export": {
            "title": "Export Report",
            "button": "Export Report",
            "subtitle": "Select the content to export.",
            "csv": "Export as CSV",
            "pdf": "Create PDF",
            "trendAnalysis": "Long-term Analysis",
            "trendDescription": "Adds an analysis of development over multiple assessments.",
            "aiAnalysis": "AI Analysis",
            "aiDescription": "Generates an Executive Summary and analyses per theme.",
            "aiDisabled": "This feature has been disabled by your administrator.",
            "question": "Which content should be included in the current report?",
            "overview": "Assessment Overview",
            "overviewDesc": "Adds the assessment overview (tiles and radar chart) to the report.",
            "trendAnalysisDesc": "Adds the long-term chart, as well as Top 3 Improvements and Top 3 Declines. Also shows a comparison value and trend for each criterion based on the selected comparison assessment.",
            "generating": "Generating...",
            "chartLoading": "Loading chart..."
        },
        "errorLoadingData": "Error loading data.",
        "scopeOwnShort": "own assessments",
        "scopeAggregate": "Aggregate of {{children}}",
        "scopeReleased": "shared assessments"
    },
    "learning": {
        "removeFromFavorites": "Remove from favorites",
        "favorites": { "title": "View favorites" },
        "cafCriteria": "CAF Themes & Criteria",
        "myFavorites": "My Favorites",
        "selectSubCriterion": "Select a criterion to view details.",
        "bestPractices": "Examples",
        "aiSuggestions": "AI Suggestions",
        "generateIdeas": "Generate New Ideas",
        "generating": "Generating...",
        "createAction": "Create Project",
        "creating": "Creating...",
        "addToFavorites": "Add to Favorites",
        "favoritesModalTitle": "My Favorites",
        "noFavorites": "You haven't added any favorites yet.",
        "noFavoritesHint": "Click the star icon next to a criterion or method to save it here for quick access.",
        "subtitle": "Knowledge base & methods for excellent administration.",
        "tabs": {
            "knowledge": "Model & Criteria",
            "methods": "Method Toolbox"
        },
        "sections": {
            "themes": "Themes and Criteria"
        },
        "methods": {
            "searchPlaceholder": "Search methods...",
            "resetTooltip": "Reset filters",
            "reset": "Reset",
            "allThemes": "All Themes",
            "allCriteria": "All Criteria",
            "shortDescription": "Brief Description",
            "tips": "Tips & Instructions",
            "reference": "Reference:",
            "adoptAction": "Create Project",
            "introTitle": "Introduction to Method"
        },
        "favoriteCriteria": "Favorite Criteria",
        "favoriteMethods": "Favorite Methods"
    },
    "validation": {
        "titleRequired": "Title is required",
        "descriptionRequired": "Description is required",
        "required": "This field is required",
        "email": "Please enter a valid e-mail address",
        "minLength": "At least {{min}} characters required",
        "nameRequired": "Name is required",
        "orgRequired": "Organization name is required",
        "roleRequired": "Role is required",
        "startDateBeforeDueDate": "Start date must not be after the due date",
        "reminderBeforeDueDate": "Reminder date must not be after the due date"
    },
    "components": {
        "labelColon": "{{label}}:",
        "toggleDropdown": "Toggle dropdown",
        "selectPlaceholder": "Select...",
        "noOptions": "No options available",
        "datePlaceholder": "Select date",
        "weekdays": ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        "monthNames": ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
        "today": "Today",
        "clearDate": "Clear",
        "prevMonth": "Previous month",
        "nextMonth": "Next month",
        "prevYear": "Previous year",
        "nextYear": "Next year"
    },
    "assessment": {
        "scoreShort": {
            "enabler": {
                "noEvidence": "No evidence",
                "plan": "PLAN",
                "do": "DO",
                "check": "CHECK",
                "act": "ACT",
                "excellence": "EXCELLENCE"
            },
            "result": {
                "noResults": "No results",
                "negativeTrends": "Negative trends",
                "stable": "Stable",
                "improvement": "Improvement",
                "targetsMet": "Targets met",
                "excellence": "Excellence"
            }
        },
        "submitConfirmMessage": "Do you really want to submit this assessment? It will become visible to the tenant and can no longer be edited.",
        "bemerkungLoeschenFrage": "Do you really want to permanently delete this remark?",
        "targetTenantPrompt": "Please enter the ID of the target tenant (client ID):",
        "reportByAuthor": "Report by {{name}}",
        "selfAssessmentsWithReleased": "Self-assessments",
        "selfAssessmentsWithReleasedIntro": "Your own self-assessments and those shared with you by subordinate units (read-only).",
        "filters": {
            "allClients": "All Clients",
            "all": "All",
            "draft": "Draft (Open)",
            "published": "Published"
        },
        "enablerScores": {
            "0-10": "No evidence or approach available.",
            "11-30": "PLAN: There are plans or ideas, but no systematic implementation yet.",
            "31-50": "DO: The approaches are implemented in relevant areas.",
            "51-70": "CHECK: The implementation is regularly reviewed and measured.",
            "71-90": "ACT: Based on reviews, improvements are made.",
            "91-100": "EXCELLENCE: Role model, benchmark, continuous improvement."
        },
        "resultScores": {
            "0-10": "No results measured or available.",
            "11-30": "Negative trends or goals are not met.",
            "31-50": "Stable results or slight improvements.",
            "51-70": "Positive trends identifiable over several years.",
            "71-90": "Goals met/exceeded, positive comparisons.",
            "91-100": "Consistently outstanding top position."
        },
        "manageAssessments": "Conduct Assessment",
        "ownSelfAssessment": "Own Self-Assessment",
        "ownSelfAssessments": "Own Self-Assessments",
        "ownSelfAssessmentsIntro": "Manage your own self-assessments or start a new cycle.",
        "unassignedBannerTitle": "Unassigned Assessments Found",
        "unassignedBannerDesc": "{{count}} assessments from previous cycles were found without an organizational unit. Assign them now to your unit ({{unitName}}):",
        "assignToMyUnitBtn": "Assign to {{unitName}} now",
        "assignSuccessMessage": "All previous assessments have been successfully assigned to your organizational unit and marked as confidential.",
        "assignErrorMessage": "Error assigning assessments.",
        "rollupDashboardTab": "Consolidated Rollup Dashboard",
        "selectOrgUnitRollup": "Organizational Unit for Consolidation",
        "assessmentOverview": "Assessment Overview",
        "assessmentOverviewIntro": "Manage your self- and external assessments or start a new cycle.",
        "newAssessment": "New Assessment",
        "assessmentFrom": "Assessment from:",
        "deleteAssessment": "Delete Assessment",
        "duplicate": "Duplicate",
        "duplicatedMessage": "Assessment duplicated. You can now make changes and save.",
        "implementedPrefix": "Implemented: ",
        "forTenant": "For Client",
        "submitErrorTitle": "Submission Error",
        "noAssessmentsFound": "No assessments found. Start your first self-assessment now.",
        "resultsOverview": "Overview of Assessment Results",
        "view": "View:",
        "compareWith": "Compare with:",
        "none": "None (Current Only)",
        "detailedAssessment": "Detailed Assessment",
        "detailedAssessmentDate": "Detailed Assessment ({{date}})",
        "addComment": "Add comment...",
        "selectOrNew": "Please select an assessment to view details, or create a new one.",
        "assessmentExists": "An assessment for today already exists.",
        "showScoringGuide": "Show Methodology & Scoring Guide",
        "backToOverview": "Back to Overview",
        "resetScores": "Reset Scores",
        "selfAssessment": "Self-Assessment",
        "externalAssessment": "External Assessment",
        "externalAssessmentIntro": "History and management of your external assessments.",
        "publish": "Publish",
        "published": "Published",
        "draft": "Draft",
        "managementSummary": "Executive Summary",
        "criterionRemark": "Remark on the Theme",
        "noExternalAssessments": "You have not created any external assessments yet.",
        "readOnly": "Read-only",
        "publishConfirmTitle": "Publish assessment?",
        "publishConfirmMessage": "It will be visible to all users of the organization. Continue?",
        "assessmentWizard": "Assessment Wizard",
        "hideAssessment": "Hide Assessment",
        "unhideAssessment": "Unhide Assessment",
        "showHidden": "Show Hidden",
        "hidden": "Hidden",
        "achtung": "Attention:",
        "folgendeKriterienWurdenImAktuellen": "The following criteria have been changed in the current model and must be completely reassessed:",
        "einreichen": "Submit",
        "wirdGespeichert": "Saving...",
        "hinzufuegen": "Add",
        "mandant": "Tenant",
        "status": "Status",
        "alsCsvExportieren": "Export as CSV",
        "alsPdfExportieren": "Export as PDF",
        "bewertungEinreichen": "Submit Assessment",
        "fassenSieDieWichtigstenErgebnisse": "Summarize the most important results and recommendations...",
        "fakultativeBemerkungZuDiesemThemenfeld": "Optional remark on this theme...",
        "massnahmeErstellen": "Create Measure",
        "bearbeiten": "Edit",
        "loeschen": "Delete",
        "staerkeVerbesserungspotenzialOderKommentarHinzufuegen": "Add strength, improvement potential, or comment...",
        "linkHinzufuegenEntfernen": "Add/Remove Link",
        "linkPlaceholder": "https://example.com or mailto:...",
        "remarkType": "Type",
        "remarkStrength": "Strength",
        "remarkPotential": "Potential",
        "remarkInfo": "Comment",
        "bemerkungLoeschen": "Delete Remark",
        "bemerkungBearbeiten": "Edit remark",
        "rated": "rated",
        "complete": "complete",
        "fillWithAI": "Fill with AI",
        "zurueckZurUebersicht": "Back to Overview",
        "infoZumKriterium": "Criterion info",
        "bewertungsassistent": "Assessment wizard",
        "neueBewertung": "New Assessment"
    },
    "assessorDashboard": {
        "dabeiSeit": "Assigned on: {{date}}",
        "archiveClient": "Archive",
        "activateClient": "Activate",
        "title": "Assessor Dashboard",
        "subtitle": "Overview of your tenants and assessments",
        "kpiClients": "Tenants",
        "kpiAssessments": "Assessments",
        "kpiDrafts": "Open Drafts",
        "kpiBestScore": "Best Score",
        "kpiWorstScore": "Worst Score",
        "kpiAvgScore": "Avg Score",
        "showInactive": "Show Inactive",
        "searchPlaceholder": "Search...",
        "noClientsFound": "No tenants found.",
        "statusInactive": "Inactive",
        "actionView": "View",
        "tableHeaderClient": "Tenant",
        "tableHeaderDate": "Date",
        "inactiveLabel": "(Inactive)",
        "myClients": "My Tenants",
        "oldestAssessments": "Oldest Assessments",
        "never": "Never",
        "errorStatusUpdate": "Error updating status."
    },
    "prioritization": {
        "loadErrorAssessments": "Error loading the assessments.",
        "loadErrorPotentials": "Error loading the improvement potentials.",
        "loadErrorProjects": "Error loading the projects.",
        "intro": "Prioritize the identified improvement potentials through team voting and urgency (score).",
        "noPotentials": "No potentials found in the current assessment.",
        "scoreLabel": "Score",
        "votesLabel": "Votes",
        "createAction": "Create Project",
        "linkProject": "Link Project",
        "projectLinked": "Transferred to project '{{project}}'",
        "projectLinkedDefault": "Transferred to project",
        "linkProjectModalTitle": "Link Project",
        "linkProjectModalDesc": "Select an existing project to integrate this improvement potential into.",
        "existingProject": "Existing Project",
        "searchProject": "Search project...",
        "link": "Link",
        "vote": "Cast Vote",
        "voted": "Voted",
        "highPriority": "High Priority",
        "mediumPriority": "Medium Priority",
        "lowPriority": "Low Priority",
        "bereitsUmgesetzteAusblenden": "Hide already implemented",
        "kontext": "Context",
        "relevanz": "Relevance?",
        "keineAktivenProjekteGefunden": "No active projects found.",
        "stimmeFuerDieseMassnahmeAbgeben": "Cast vote for this measure",
        "basis": "Based on",
        "noProjectsForQuery": "No projects found for \"{{query}}\"."
    },
    "contentManagement": {
        "methodSaveError": "An unknown error occurred (possibly while uploading the image).",
        "cleanModalMessage": "Do you really want to delete all your own themes, criteria and examples?",
        "filters": {
            "allThemes": "All themes",
            "allCriteria": "All criteria"
        },
        "title": "Content Management",
        "intro": "Manage and customize the structure, themes, and criteria of the CAF application.",
        "editTitle": "Edit Title",
        "editDescription": "Edit Description",
        "saveChanges": "Save Changes",
        "criterionDescription": "Theme Description",
        "subCriteria": "Criteria",
        "bestPracticesLabel": "Examples",
        "bestPracticesPlaceholder": "Enter each example on a new line.",
        "addSubCriterion": "Add Criterion",
        "addCriterion": "Add New Theme",
        "subCriterionDescriptionPlaceholder": "Description of the criterion...",
        "enabler": "Enabler",
        "result": "Result",
        "deleteCriterion": "Delete Theme",
        "deleteSubCriterion": "Delete Criterion",
        "addBestPractice": "Add Example",
        "deleteBestPractice": "Delete",
        "cleanCustomContent": "Delete Own Content",
        "cleanModalTitle": "Delete Own Content",
        "newTheme": "New Theme",
        "newCriterion": "New Criterion",
        "newExample": "New Example",
        "newModule": "New Module",
        "newSection": "New Section",
        "translation": {
            "readingFile": "Reading file...",
            "invalidJson": "Invalid JSON format.",
            "unknownFileType": "Unknown file type. Expected: fileType “structure” or “methods”.",
            "unsupportedLanguage": "Unsupported target language: “{{lang}}”. Allowed: en, fr, it.",
            "validatingStructure": "Validating structure file...",
            "validatingMethods": "Validating methods file...",
            "invalidFile": "Invalid file: {{detail}}",
            "insertingThemes": "Inserting translations for themes...",
            "insertingModules": "Inserting translations for info modules...",
            "insertingMethods": "Inserting translations for methods...",
            "savingTranslations": "Saving translations...",
            "structureImported": "Structure translation ({{lang}}) imported successfully.",
            "methodsImported": "Methods translation ({{lang}}) imported successfully.",
            "importFailed": "Import failed: {{msg}}",
            "openModal": "Export / Import Translation",
            "modalTitle": "Content for Translation",
            "modalSubtitle": "Export all texts as JSON for AI-assisted translation, then import the translated result.",
            "exportTab": "Export",
            "importTab": "Import",
            "targetLanguage": "Target Language (AI context)",
            "importLanguage": "Language of Translation",
            "fieldCount": "{{count}} text fields will be exported",
            "exportHint": "Download the JSON and pass it to an AI with this prompt:",
            "aiPrompt": "Translate all texts in this JSON into {{language}}. Keep the exact JSON structure, all keys, and all values of _id and _index fields unchanged.",
            "downloadJson": "Download JSON",
            "importWarningTitle": "Attention",
            "importHint": "Existing translations for this language will be overwritten. German source texts remain unchanged.",
            "importJson": "Import JSON",
            "importing": "Importing...",
            "fileStructure": "File 1: CAF Structure & Info Modules",
            "fileMethods": "File 2: Method Toolbox",
            "importBothHint": "Import both files separately (structure file and methods file)."
        }
    },
    "superAdmin": {
        "migration": {
            "loadingData": "Loading data...",
            "analyzingAssets": "Analyzing assets...",
            "packingAsset": "Packing asset {{current}}/{{total}}",
            "creatingZip": "Creating ZIP file...",
            "exportDone": "Export complete. Assets saved: {{n}}.",
            "exportFailedAssets": "WARNING: Some files could not be loaded (count: {{n}}).\nThis is usually caused by a missing CORS configuration on the storage bucket.\nSee the browser console for details.",
            "exportFailed": "Export failed: {{msg}}",
            "analyzingZip": "Analyzing ZIP file...",
            "zipMissingData": "Invalid ZIP: “data.json” is missing.",
            "isZipFile": "This is a ZIP file. Please import it as .zip.",
            "noCafStructure": "Invalid data: no CAF structure found.",
            "confirmImport": "Import? This overwrites the global settings in the system.",
            "assetsWillUpload": "Assets will be uploaded.",
            "importingAssets": "Importing assets (done: {{n}})...",
            "missingInZip": "Missing in ZIP: {{file}}",
            "processingData": "Processing data (this may take a moment)...",
            "importWarnings": "Import completed with warnings.\n\nAssets transferred: {{ok}}\nFailed: {{failed}}",
            "importDoneAssets": "Import successful. Assets transferred: {{n}}.",
            "savingDatabase": "Saving database...",
            "importDone": "Data imported successfully. Please reload the page.",
            "importFailed": "Import failed: {{msg}}"
        },
        "tenantCreatedSuccess": "Tenant “{{orgName}}” has been created. The tenant admin will receive an invitation by email.",
        "title": "Tenant Management",
        "intro": "Manage all organizations, their licensing, and accesses.",
        "createTenant": "Create New Tenant",
        "tenantName": "Organization Name",
        "adminEmail": "Admin E-Mail",
        "status": "Status",
        "plan": "Plan",
        "actions": "Actions",
        "active": "Active",
        "inactive": "Inactive",
        "created": "Created on",
        "modalTitle": "Create New Tenant",
        "toggleStatus": "Change Status",
        "createTenantTitle": "Create New Tenant",
        "createUserTitle": "Invite User",
        "editUserRoleTitle": "Change Role",
        "deleteTenantTitle": "Delete Tenant",
        "deleteTenantMessage": "Are you sure? This action will permanently delete the tenant and ALL associated data.",
        "deleteTenantConfirmationLabel": "Enter the organization name to confirm:",
        "deleteAssessorTitle": "Delete Assessor",
        "deleteAssessorMessage": "Are you sure? This action will permanently delete the assessor account.",
        "deleteAssessorConfirmationLabel": "Enter the e-mail address to confirm:",
        "importExport": "Import / Export Data",
        "exportGlobal": "Export global dataset (incl. assets)",
        "importGlobal": "Restore Backup",
        "importWarning": "Warning: Overwrites current data!",
        "fullDelete": "Complete Deletion",
        "tenantDeleteButton": "Delete tenant completely",
        "assessorDeleteButton": "Delete assessor completely",
        "sso": {
            "title": "Single Sign-On (SAML 2.0)",
            "description": "Configure SSO with Microsoft Entra ID / Azure AD for this tenant.",
            "enabled": "SSO Enabled",
            "disabled": "SSO Disabled",
            "enableToggle": "Enable SAML SSO",
            "displayName": "Display Name",
            "displayNamePlaceholder": "e.g., City of Bern SSO",
            "entityId": "IdP Entity ID",
            "entityIdPlaceholder": "e.g., https://sts.windows.net/{tenant-id}/",
            "ssoUrl": "SSO Login URL",
            "ssoUrlPlaceholder": "https://login.microsoftonline.com/{tenant-id}/saml2",
            "certificate": "X.509 Certificate (PEM)",
            "certificatePlaceholder": "-----BEGIN CERTIFICATE-----\\n...\\n-----END CERTIFICATE-----",
            "domains": "SSO Domains",
            "domainsPlaceholder": "city-bern.ch, municipality.ch",
            "domainsHint": "Comma-separated email domains that will sign in via SSO.",
            "save": "Configure SSO",
            "saving": "Saving...",
            "remove": "Remove SSO",
            "removing": "Removing...",
            "configured": "SSO Configured",
            "notConfigured": "SSO Not Configured",
            "saveSuccess": "SSO configuration successfully saved.",
            "removeSuccess": "SSO configuration removed.",
            "removeConfirm": "Do you really want to disable SSO for this tenant? Users will then have to log in with e-mail/password.",
            "rpEntityId": "SP Entity ID (optional)",
            "rpEntityIdPlaceholder": "Leave blank for auto-generation"
        },
        "assessorDeleted": "Assessor \"{{email}}\" has been permanently deleted.",
        "errorChangeStatus": "Error changing Assessor status.",
        "orgSummary": "Organization",
        "orgSummaryValue": "{{units}} units · {{levels}} levels",
        "events": {
            "title": "Changes in tenants ({{count}})",
            "intro": "Tenant admins can extend their structure themselves and rename their tenant. Check whether the contract and billing need to be adjusted, then acknowledge the notice.",
            "acknowledge": "Acknowledged",
            "loadError": "Notices could not be loaded.",
            "ackError": "Acknowledging failed."
        }
    },
    "modals": {
        "ok": "OK",
        "close": "Close",
        "save": "Save",
        "cancel": "Cancel",
        "apply": "Apply",
        "confirmDelete": "Confirm Deletion",
        "confirmReset": "Confirm Reset",
        "resetScoresTitle": "Reset Scores",
        "resetScoresMessage": "Are you sure you want to remove all awarded points in this assessment? Your texts (strengths/improvement potentials) will be retained.",
        "assessmentInfoTitle": "CAF Assessment Methodology",
        "criterionInfoTitle": "Criterion Details",
        "enablerScoringTitle": "Enabler Scoring",
        "enablerScoringIntro": "Scoring is based on the PDCA cycle (Plan-Do-Check-Act). Rate the maturity level of implementation:",
        "enablerScoringDetails": {
            "plan": "<strong>PLAN (0-30 Points):</strong> Planning phase. There are approaches or ideas, but no systematic implementation yet.",
            "do": "<strong>DO (31-50 Points):</strong> Implementation. The approaches are applied in relevant areas.",
            "check": "<strong>CHECK (51-70 Points):</strong> Review. The effectiveness of the implementation is regularly measured and reviewed.",
            "act": "<strong>ACT (71-90 Points):</strong> Improvement. Based on reviews, corrective actions are initiated and processes optimized.",
            "excellent": "<strong>EXCELLENCE (91-100 Points):</strong> The approach serves as a role model (benchmark) and is continuously improved."
        },
        "resultScoringTitle": "Results Scoring",
        "resultScoringIntro": "This evaluates the results achieved by the organization. Pay special attention to trends over the last 3 years:",
        "resultScoringDetails": {
            "none": "<strong>0-10 Points:</strong> No results measured or available.",
            "negative": "<strong>11-30 Points:</strong> Results exist but show negative trends or do not achieve goals.",
            "stable": "<strong>31-50 Points:</strong> Results are stable or show slight improvements.",
            "positive": "<strong>51-70 Points:</strong> Results show a clear positive trend over several years.",
            "targets": "<strong>71-90 Points:</strong> Relevant goals are met or exceeded. Positive comparisons with other organizations.",
            "excellent": "<strong>91-100 Points:</strong> Outstanding results over time and top position in comparisons."
        },
        "editAction": "Edit Measure",
        "addAction": "Add New Measure",
        "title": "Title",
        "description": "Description",
        "relatedSubCriterion": "Related CAF Criterion",
        "selectSubCriterion": "Select a Criterion",
        "responsiblePerson": "Responsible Person",
        "selectResponsible": "Select Person...",
        "noResponsible": "No Responsible Assigned",
        "dueDate": "Due Date",
        "reminderDate": "Reminder Date",
        "reminderHint": "Auto: 14 days before due date (min. today)",
        "status": "Status",
        "deleteActionTitle": "Delete Measure",
        "deleteActionMessage": "Are you sure you want to delete this measure?",
        "deleteAssessmentTitle": "Delete Assessment",
        "deleteAssessmentMessage": "Are you sure you want to delete the assessment from <strong>{{date}}</strong>?",
        "deleteCriterionTitle": "Delete Theme",
        "deleteCriterionMessage": "Are you sure you want to delete the theme \"<strong>{{title}}</strong>\"?",
        "deleteCriterionWarning": "This will also permanently delete all associated criteria and their assessment data.",
        "deleteSubCriterionTitle": "Delete Criterion",
        "deleteSubCriterionMessage": "Are you sure you want to delete the criterion \"<strong>{{title}}</strong>\"?",
        "deleteSubCriterionWarning": "This will also permanently delete all associated assessment data.",
        "deleteBestPracticeMessage": "Are you sure you want to delete this example?",
        "editUserTitle": "Edit User",
        "editProfileTitle": "Edit Profile",
        "editUserMessage": "Edit the user's details.",
        "createMeasure": {
            "title": "Project Selection",
            "forPotential": "for potential:",
            "done": "Done",
            "manual": "Formulate project manually",
            "aiSuggestions": "AI Suggestions",
            "generateAi": "Generate AI Suggestions",
            "analyzing": "Analyzing potential & criterion...",
            "relevantMethods": "Methods for the criterion",
            "noMethods": "No specific methods found for this criterion.",
            "adoptMethod": "Create Project",
            "methodLabel": "Method"
        },
        "edit": "Edit",
        "delete": "Delete"
    },
    "common": {
        "serviceUnavailable": "The service is currently unavailable. Please try again later.",
        "remove": "Remove",
        "open": "Open",
        "ai": {
            "suggestionLabel": "AI Suggestion",
            "backToOverview": "Back to Overview",
            "adoptSuggestion": "Create Project",
            "generateTitleTooltip": "Generate title based on description"
        },
        "sessionWarning": {
            "title": "Your session will expire soon",
            "message_one": "For security reasons, you will be automatically logged out in 1 minute. Do you want to stay logged in?",
            "message_other": "For security reasons, you will be automatically logged out in {{minutes}} minutes. Do you want to stay logged in?",
            "extendButton": "Stay logged in",
            "logoutButton": "Log out now"
        },
        "notAvailable": "N/A",
        "assessmentSaved": "Assessment saved successfully!",
        "actionCreated": "Action created!",
        "actionUpdated": "Action updated!",
        "noAssessmentSelected": "No assessment selected to save.",
        "saving": "Saving...",
        "processing": "Processing...",
        "allChangesSaved": "All changes saved",
        "verify": "Verify",
        "cancel": "Cancel",
        "viewDetails": "View Details",
        "description": "Description",
        "success": "Success",
        "offlineWarningTitle": "No Internet Connection",
        "offlineWarningMessage": "You are currently offline. Please restore your internet connection and try again. Your input will not be lost.",
        "offlineMoveTaskWarning": "Tasks cannot be moved without an internet connection. Please restore your connection.",
        "errors": {
            "pdfGeneration": "Error generating PDF.",
            "cleanup": "Error during cleanup.",
            "save": "Error saving: {{msg}}",
            "delete": "Error deleting.",
            "archiveTasks": "Error archiving tasks.",
            "saveTask": "Error saving task.",
            "saveVote": "Error saving vote: {{msg}}",
            "saveProject": "Error saving project: {{msg}}",
            "linkProject": "Error linking project: {{msg}}",
            "pdfExport": "Error exporting PDF.",
            "publish": "Error publishing.",
            "updateStatus": "Error updating status.",
            "functionsUnavailable": "Error: Connection to Cloud Functions is unavailable.",
            "functionsUnavailableShort": "Cloud Functions unavailable.",
            "changeRole": "Error: {{msg}}",
            "createLabel": "Error creating label.",
            "moveTask": "Error moving task.",
            "saveTaskMsg": "Error saving task: {{msg}}",
            "sendComment": "Error sending comment",
            "backendMissing": "Error: Backend connection missing.",
            "deleteUser": "Error deleting user.",
            "deleteMsg": "Error deleting: {{msg}}",
            "generic": "An error occurred"
        },
        "linkCopied": "Link copied!",
        "sending": "Sending...",
        "selectedCount": "{{count}} selected",
        "saveChanges": "Save changes",
        "send": "Send",
        "view": "View",
        "unknownError": "Unknown Error",
        "unknownTenant": "Unknown Tenant",
        "unknownLabel": "Unknown Label",
        "unknownCategory": "Unknown Category",
        "unknownDate": "Unknown",
        "ourOrganization": "Our Organization",
        "recordedOn": "Recorded on {{date}} by {{name}}",
        "openMenu": "Open menu",
        "clearSearch": "Clear search",
        "toggleDetails": "Toggle details",
        "mainContent": "Main content",
        "sidebarNavigation": "Sidebar navigation",
        "mainNavigation": "Main navigation",
        "skipToContent": "Skip to content",
        "landmarkNavHint": "Press F6 to switch between navigation and content",
        "close": "Close",
        "save": "Save",
        "collapse": "Collapse",
        "expand": "Expand",
        "edit": "Edit",
        "delete": "Delete",
        "error": "Error",
        "retry": "Try again",
        "back": "Back",
        "moveUp": "Move up",
        "moveDown": "Move down",
        "errorOccurred": "An error occurred.",
        "next": "Next",
        "loading": "Loading..."
    },
    "userManagement": {
        "displayName": "Display Name",
        "title": "User Management",
        "subtitle": "Manage user access and external assessors for your organization.",
        "tabs": {
            "internal": "Employees",
            "external": "External Assessors"
        },
        "titles": {
            "registered": "Registered Users",
            "associated": "Associated Assessors"
        },
        "buttons": {
            "inviteUser": "Invite User",
            "addAssessor": "Add Assessor",
            "invite": "Invite",
            "sending": "Sending...",
            "adding": "Adding...",
            "add": "Add",
            "cancel": "Cancel",
            "edit": "Edit",
            "remove": "Remove",
            "save": "Save",
            "saving": "Saving..."
        },
        "labels": {
            "email": "E-Mail Address",
            "assessorEmail": "Assessor's E-Mail Address",
            "permissionLevel": "Permission Level",
            "changeLevelPrefix": "Change",
            "changeLevelSuffix": "level...",
            "selectLevelSuffix": "Select...",
            "emailLanguage": "Invitation Language",
            "orgUnit": "Organization Unit"
        },
        "placeholders": {
            "email": "employee@org.com",
            "assessorEmail": "assessor@external.com",
            "name": "John Doe",
            "selectOrgUnit": "Main Organization (Tenant-wide)",
            "chooseOrgUnit": "Select unit"
        },
        "filters": {
            "allOrgUnits": "All Organization Units",
            "filterByOrgUnit": "Filter by Organization Unit"
        },
        "badges": {
            "tenantAdmin": "Tenant-Admin",
            "mainOrg": "Main Organization",
            "tenantWide": "Tenant-wide",
            "orgAdmin": "Org-Admin",
            "noOrgUnit": "No unit"
        },
        "hints": {
            "isOrgAdmin": "Allows this user to invite and manage colleagues within the selected organization unit.",
            "isOrgAdminFixed": "You are inviting this user as a member of your organization unit."
        },
        "table": {
            "nameEmail": "Name / E-Mail",
            "orgUnit": "Organization Unit",
            "roleStatus": "Role / Status",
            "actions": "Actions",
            "noUsers": "No users found.",
            "noAssessors": "No external assessors associated.",
            "caption": "User list"
        },
        "roles": {
            "externalAssessor": "External Assessor",
            "external": "External"
        },
        "modals": {
            "editUserTitle": "Edit User",
            "userUpper": "USER",
            "noName": "No Name",
            "inviteSuccessTitle": "User Invited",
            "addSuccessTitle": "Successfully Added",
            "emailSent": "E-mail sent successfully!",
            "activationInfo": "The user will receive a link to set their password and activate the account.",
            "emailFailed": "Account created, but e-mail failed.",
            "emailFailedHint": "The user was created in the system, but we could not send an e-mail. Please ask the user to click 'Forgot Password' on the login page.",
            "removeAssessorTitle": "Remove Assessor",
            "revokeAccessTitle": "Revoke Access",
            "removeAssessorConfirm": "Do you want to remove the assessor {{email}} from this tenant?",
            "revokeAccessConfirm": "Do you want to revoke access for user {{email}} from this organisation?",
            "checkingDependencies": "Checking for assigned projects and tasks...",
            "orphanedWarningTitle": "Warning: Orphaned Entries!",
            "orphanedWarningDetail": "This user is still assigned to:",
            "projectsCount": "{{count}} project(s) as project lead",
            "tasksCount": "{{count}} task(s) as responsible person",
            "orphanedConsequence": "Deleting this user will leave these entries without a valid responsibility.",
            "removeAssessorNote": "The assessor will lose access to this tenant. Their other tenants are not affected.",
            "revokeAccessNote": "The profile will be deleted and the login deactivated.",
            "userExistsDetail": "is already registered in the system.",
            "userExistsTip": "If this is an assessor, please try the 'External Assessors' tab.",
            "notFoundDetail": "found.",
            "assessorRegisterHint": "The assessor must first register themselves in the system ('Register as Assessor' on the login page) before they can be added to a tenant."
        },
        "validation": {
            "invalidEmail": "Invalid e-mail address",
            "nameRequired": "Name is required",
            "orgUnitRequired": "Organizational unit required"
        },
        "errors": {
            "permissionDenied": "Access denied.",
            "loadFailed": "Error loading users.",
            "inviteFailed": "Error sending invitation",
            "addFailed": "Error adding assessor",
            "assessorInactive": "This assessor account is deactivated and cannot be added.",
            "notAnAssessor": "This e-mail belongs to an existing user who is NOT an assessor.",
            "assessorAlreadyAdded": "This assessor has already been added.",
            "cannotChangeRole": "Could not change role.",
            "deleteFailed": "Deletion failed."
        },
        "messages": {
            "assessorInvited": "An invitation e-mail has been sent. The assessor can now complete their registration and will then have access.",
            "assessorAlreadyAdded": "This assessor is already assigned.",
            "assessorAdded": "The assessor was successfully added and can now submit assessments for this tenant."
        },
        "noRoles": "No functional role"
    },
    "settings": {
        "projectSpecificTitle": "Settings of individual projects",
        "projectSpecificHint": "Project members, labels and project-specific fields for tasks are set in each project via the gear icon at the top right. This is done by the project lead and the portfolio management of the unit where the project originated. Parent units see forwarded projects read-only.",
        "toggleSr": "On/Off",
        "ok": "OK",
        "introUnit": "Categories of your unit for the project portfolio.",
        "title": "Settings",
        "intro": "Manage global configurations for your organization.",
        "mfaTitle": "2-Factor Authentication (MFA)",
        "mfaToggleLabel": "Enforce multi-factor authentication (MFA) for all users",
        "mfaDescription": "If enabled, all users of this organization must set up an authenticator app (TOTP) before they can continue using CAF Suite.",
        "mfaSsoExemptNote": "SSO users are exempt from the MFA requirement because identity verification is managed by the SSO provider.",
        "mfaDialog": {
            "setupOwnFirst": { "title": "Secure your own account first", "message": "Before you enforce MFA for everyone, set up an authenticator app for your own account. This way you cannot lock yourself out. Then turn on the requirement here.", "confirm": "Set up MFA now" },
            "enable": { "title": "Enforce MFA for everyone?", "message": "Anyone who has not set up an authenticator app yet is taken to the setup immediately and can only use CAF Suite again afterwards.", "confirm": "Enforce MFA" },
            "disable": { "title": "Lift the MFA requirement?", "message": "A second factor will then be optional. Authenticator apps already set up remain active.", "confirm": "Lift requirement" }
        },
        "analysisTitle": "Artificial Intelligence",
        "analysisToggleLabel": "Enable AI Features",
        "analysisDescription": "If enabled, users can use AI features (e.g., generation of project ideas or AI-based reports).",
        "prioritizationTitle": "Prioritization Module",
        "prioritizationToggleLabel": "Enable Prioritization of Potentials",
        "prioritizationDescription": "Enables a module between Assessment and Action Plan to pre-select potentials through team voting and urgency.",
        "warningTitle": "Important Note on Data Privacy",
        "warningText": "For analysis, anonymized assessment data, remarks, and project titles are sent to the AI API. No user or personal data is transmitted. Disable this feature if your organization prohibits the use of cloud-based AI services for this type of data.",
        "saveTitle": "Save Project Settings",
        "saveSuccess": "Settings saved.",
        "saveError": "Error saving.",
        "dataExport": {
            "title": "Data Export",
            "description": "Download all assessments, projects, and measures of your organization as a JSON file.",
            "button": "Export Data",
            "loading": "Creating export...",
            "success": "Export successfully downloaded.",
            "error": "Error exporting data."
        },
        "promise": "Promise",
        "modulProjektmanagement": "Project Management Module",
        "achsenImPortfoliomanagementAendern": "Change axes in portfolio management",
        "wechseltImBubbleChartIn": "Changes the Bubble Chart to the alternative view (x: Strategic Alignment, y: Estimated Effort, Bubble Size: Benefit).",
        "wipLimitsAktivieren": "Enable WIP Limits",
        "wipLimitsDescription": "Controls the number of simultaneous projects in the \"In Progress\" phase in the Portfolio Kanban using defined capacity buckets.",
        "kapazitaetsToepfeBucketsKonfiguration": "Capacity Buckets Configuration",
        "keineBucketsDefiniertBitteVorlage": "No buckets defined. Please adjust template.",
        "bucketName": "Bucket Name",
        "bucketNamePlaceholder": "e.g. Large Projects",
        "addBucket": "Add Bucket",
        "projektgroessenTShirt": "Project Sizes (T-Shirt)",
        "wipLimit": "WIP Limit",
        "projektrisikenAktivieren": "Enable Project Risks",
        "aktiviertDasModulProjektrisikenUm": "Enables the Project Risks module to record project-related risks with damage extent and probability.",
        "taskManagementAktivieren": "Enable Task Management",
        "aktiviertDasModulTaskManagement": "Enables the Task Management module to flexibly manage tasks per project in lists, as a board, or on a timeline.",
        "modulBewertung": "Assessment Module",
        "modulAdministration": "Administration Module",
        "inhaltsverwaltungAktivieren": "Enable Content Management",
        "contentManagementDescription": "Enables the \"Content Management\" module for administrators to add custom themes, criteria, and/or examples to the official CAF criteria.",
        "availableLanguages": "Available Languages",
        "availableLanguagesDescription": "Define which languages are available for users of this tenant to select in their profile.",
        "loeschen": "Delete"},
    "app": {
        "globalTemplatesTitle": "Manage Global Templates",
        "globalTemplatesIntro": "Define the standard structure (criteria & best practices) and the information sections for all tenants here.",
        "connection": {
            "connectingTitle": "Connecting …",
            "connectingMessage": "The connection is slow. Please wait a moment.",
            "failedTitle": "Connection failed",
            "failedMessage": "You are signed in, but your data could not be loaded, usually because of a slow or interrupted internet connection. We will keep trying automatically.",
            "retry": "Try again",
            "retrying": "Trying …"
        },
        "update": { "title": "New version available", "description": "An updated version of CAF Suite is ready. Please save your work and update the app.", "later": "Later", "now": "Update now" },
        "loading": "Loading...",
        "viewNotFound": "View not found",
        "errors": {
            "noTenant": {
                "title": "No Organization Assigned",
                "message": "Your user account is not assigned to any organization. Please contact support."
            },
            "accountDisabled": {
                "title": "Account Locked / Inactive",
                "message": "Your user account has been deactivated. Please contact support or your administrator for more information.",
                "logout": "Sign Out"
            },
            "emailSendFailed": "Error sending: {{msg}}",
            "emailSendUnknown": "Error sending: Unknown error"
        },
        "navigationGuard": {
            "title": "Unsaved Changes",
            "message": "You have made changes that have not been saved yet.",
            "warning": "Do you really want to leave the page? All unsaved changes will be lost.",
            "confirmButton": "Leave Anyway"
        },
        "verifyEmail": {
            "title": "E-Mail Verification Required",
            "prompt": "Please verify your e-mail address",
            "spamFolder": "to continue. Also check your spam folder.",
            "confirmReload": "I have confirmed (reload page)",
            "successMessage": "E-Mail has been resent!",
            "resendLink": "Resend verification link"
        },
        "projectSpecificTitle": "Settings of individual projects",
        "projectSpecificHint": "Project members, labels and project-specific fields for tasks are set in each project via the gear icon at the top right. This is done by the project lead and the portfolio management of the unit where the project originated. Parent units see forwarded projects read-only."
    },
    "errorBoundary": {
        "title": "An Error Occurred",
        "message": "The application could not be started. This is usually caused by network problems, missing permissions, or an outdated configuration.",
        "reload": "Reload Page"
    },
    "task": {
        "title": "Measures (Tasks)",
        "open": "Open",
        "inProgress": "In Progress",
        "review": "Review",
        "done": "Done",
        "labels": "Labels",
        "effortEstimate": "Effort",
        "customFields": "Project-specific Fields"
    },
    "projectRisks": {
        "loadError": "Error loading the project risks.",
        "matrixPosition": "Damage: {{damage}}, probability: {{probability}}",
        "matrixCellTitle_one": "Damage: {{damage}}, probability: {{probability}} ({{count}} risk)",
        "matrixCellTitle_other": "Damage: {{damage}}, probability: {{probability}} ({{count}} risks)",
        "taskFromRiskTitle": "Measure for risk: {{name}}",
        "title": "Project Risks",
        "description": "Manage risks with extent of damage and probability of occurrence.",
        "addRisk": "Record New Risk",
        "editRisk": "Edit Risk",
        "deleteRiskTitle": "Delete Risk",
        "deleteRiskMessage": "Are you sure you want to delete this risk?",
        "name": "Risk Name",
        "riskDescription": "Risk Description (credible worst-case)",
        "damage": "Extent of Damage",
        "probability": "Probability",
        "history": "History",
        "generateTask": "Generate Measure",
        "tasks": "Derived Measures",
        "matrixTitle": "Risk Matrix",
        "listTitle": "Risk List",
        "noRisks": "No risks recorded.",
        "save": "Save",
        "cancel": "Cancel",
        "highRisk": "High Risk",
        "damageOptions": {
            "1": "1 - Low",
            "2": "2 - Noticeable",
            "3": "3 - Critical",
            "4": "4 - Catastrophic"
        },
        "probabilityOptions": {
            "1": "1 - Unlikely",
            "2": "2 - Possible",
            "3": "3 - Probable",
            "4": "4 - Almost Certain"
        },
        "noTasksYet": "No tasks linked yet.",
        "oneMeasure": "1 measure",
        "measuresCount": "{{count}} measures",
        "damage1": "1 - Low",
        "damage2": "2 - Noticeable",
        "damage3": "3 - Critical",
        "damage4": "4 - Catastrophic",
        "prob1": "1 - Unlikely",
        "prob2": "2 - Possible",
        "prob3": "3 - Likely",
        "prob4": "4 - Almost certain"
    },
    "projectSettings": {
        "labelsLoadError": "Error loading the labels.",
        "deleteLabelError": "Error deleting the label.",
        "tasksTab": "Measures",
        "customFieldsTitle": "Project-specific Fields",
        "customFieldsDescription": "Define up to 3 additional fields for measures in this project.",
        "fieldLabel": "Field Name",
        "fieldType": "Field Type",
        "fieldTypeText": "Text Field",
        "fieldTypeCheckbox": "Checkbox",
        "fieldTypeDropdown": "Dropdown",
        "dropdownOptions": "Selection Options",
        "dropdownOptionsHint": "Comma-separated (e.g. Option A, Option B)",
        "addField": "+ Add Field",
        "maxFieldsReached": "Maximum 3 fields allowed.",
        "wipBucketsConfig": "Capacity Buckets Configuration",
        "taskGroupsTitle": "Measure Groups",
        "taskGroupsDescription": "Group measures into phases or goals (e.g., Phase 1, Phase 2). These groups can be visualized on the roadmap.",
        "addGroup": "+ Add Group",
        "groupName": "Group Name (e.g., Phase 1)",
        "groupDeleteWarning": "Warning: Measures in this group will be assigned to 'None'.",
        "taskWipLimitTitle": "WIP Limit (Measures)",
        "taskWipLimitDescription": "Controls the number of concurrent tasks in the 'In Progress' phase using defined capacity buckets.",
        "taskWipLimitLabel": "Maximum Number",
        "taskWipLimitDisabled": "No Limit",
        "saveSettings": "Save Settings",
        "membersSaveError": "Error saving members.",
        "measuresSaveError": "Error saving measure settings.",
        "membersDescription": "Select who can access this project and collaborate on tasks. Level-30 users and administrators have access to all projects by default.",
        "addBucket": "+ Add bucket",
        "automZugriff": "Autom. Access (Lvl {{level}})",
        "membersFooterHint": "Changes will only take effect after saving.",
        "tasksFooterHint": "Fields and WIP limits are saved per project.",
        "labelsFooterHint": "Labels are created and deleted immediately.",
        "updateLabelError": "Error updating label.",
        "membersDescriptionV2": "Select who may access this project and work on measures. Portfolio management and the management of the originating unit have access in any case.",
        "automZugriffPortfolio": "Auto access (portfolio)",
        "pendingNew": "New",
        "keineGruppen": "No measure groups defined.",
        "openMeasure": "open measure",
        "openMeasures": "open measures"
    },
    "tasks": {
        "noDate": "NO DATE",
        "today": "Today",
        "unnamed": "Untitled",
        "boardReadOnly": "Read only: cards on this board cannot be moved.",
        "kpiMeasures": "Measures",
        "boardSorts": {
            "label": "Sort columns",
            "manual": "Manual sort",
            "alphabetical": "A-Z",
            "dueDate": "Due Date",
            "taskSize": "Task Size"
        },
        "promise": "Promise",
        "reset": "Reset",
        "neueAufgabe": "New Task",
        "viewMode": {
            "timeline": "Timeline"
        },
        "searchPlaceholder": "Search by title, description...",
        "filters": {
            "responsibleAll": "Responsible (All)",
            "labelAll": "Label (All)",
            "labelPrefix": "Label",
            "dueDateAll": "Due Date (All)",
            "dueDateNext30": "Due in next 30 days",
            "dueDateOverdue": "Overdue"
        },
        "status": {
            "TODO": "Open",
            "IN_PROGRESS": "In Progress",
            "REVIEW": "Review",
            "DONE": "Done"
        },
        "report": {
            "taskCount_one": "{{count}} measure",
            "taskCount_other": "{{count}} measures",
            "title": "Measure Report"
        }
    },
    "myTasks": {
        "title": "My Tasks",
        "intro": "Here you can find all tasks assigned to you.",
        "kpi": {
            "open": "Open Tasks",
            "dueSoon": "Due (30 days)",
            "overdue": "Overdue",
            "openSubtext": "Neither completed nor archived",
            "dueSoonSubtext": "Due within the next 30 days",
            "overdueSubtext": "Due date already passed"
        },
        "list": {
            "taskName": "Task",
            "project": "Project",
            "startDate": "Start Date",
            "dueDate": "Due Date",
            "status": "Status"
        },
        "filters": {
            "search": "Search...",
            "project": "Project",
            "status": "Status",
            "reset": "Reset",
            "projectAll": "Project (All)"
        },
        "noTasks": "You currently have no tasks assigned."
    },
    "portfolio": {
        "keineProjektleitung": "No project lead",
        "loadError": "Error loading the projects.",
        "wipLimitWarning": "Attention: The configured capacity limit for this project size has already been reached. Moving additional projects into implementation typically causes bottlenecks and delays for ongoing initiatives.",
        "wipLimitQuestion": "Do you still want to move this project into implementation because of urgent priorities or an escalation?",
        "filterClear": "Clear Filter",
        "healthAtRisk": "Critical Pulse",
        "healthOnTrack": "On Track",
        "title": "Project Portfolio",
        "searchPlaceholder": "Search by title, description, project lead...",
        "intro": "Manage your strategic implementation projects.",
        "newProject": "New Project",
        "noProjects": "No projects available yet. Start implementing your CAF goals.",
        "ungueltigeVerantwortlichkeitTitle": "Invalid Responsibility",
        "statusLabel": "Status:",
        "status": {
            "IDEA": "Idea",
            "EVALUATING": "Evaluating",
            "IN_PROGRESS": "In Progress",
            "ON_HOLD": "On Hold",
            "COMPLETED": "Completed",
            "ARCHIVED": "Archived"
        },
        "kpi": {
            "total": "All Projects",
            "active": "Active",
            "completed": "Completed",
            "atRisk": "Critical Pulse",
            "decisionNeeded": "Decisions"
        },
        "filters": {
            "originUnitGroup": "Originating unit",
            "ownCategories": "Own categories",
            "topicCategories": "Cross-cutting topics",
            "all": "All (excl. archived)",
            "active": "Active Only",
            "decision": "Decisions",
            "missingRatings": "Missing Ratings",
            "category": "Category",
            "save": "Save Filter",
            "saveTooltip": "Save current filter selection for this user",
            "saved": "Saved",
            "categoryAll": "Category (All)",
            "allOrgUnits": "Organizational Unit (All)",
            "relationAll": "Origin (All)",
            "relationOwn": "Own unit",
            "relationPromoted": "Forwarded",
            "relationTopic": "Cross-cutting topic"},
        "itPortfolioFilter": "IT Projects Only",
        "boardSorts": {
            "label": "Sorting",
            "manual": "Manual",
            "alphabetical": "Alphabetical",
            "dueDate": "Target Date",
            "projectSize": "Project Size"
        },
        "matrixWarning": "Some projects are not displayed in the matrix because they lack effort estimation, strategic importance, or benefit.",
        "unratedProjectsWarning": "The following projects have not yet been rated and therefore cannot be displayed in the chart.",
        "showMissing": "View in list view",
        "viewMode": {
            "list": "List",
            "matrix": "Matrix",
            "kanban": "Kanban",
            "roadmap": "Road Map"
        },
        "export": {
            "page": "Page",
            "projectOverviewTitle": "Project Overview",
            "tableHeaderProject": "Project",
            "tableHeaderPulseCheck": "Pulse Check",
            "chartRenderError": "Chart could not be rendered.",
            "noCafReference": "No CAF Reference",
            "trafficLightsTitle": "Pulse Check (Traffic Lights):",
            "button": "Export Current View",
            "title": "Export Portfolio as PDF",
            "subtitle": "The export adopts the current view, filters, and sorting.",
            "includeDetails": "Include project details",
            "includeStatus": "Include project status (traffic lights, executive summary, decisions)",
            "groupBy": "Group project details by",
            "groupByNone": "No grouping",
            "groupByStatus": "Status",
            "groupByResponsible": "Responsibility",
            "groupByCafCriterion": "CAF Criterion",
            "generating": "Generating...",
            "create": "Create PDF",
            "cancel": "Cancel",
            "reportTitle": "Project Portfolio Report",
            "organization": "Organization:",
            "viewModeLabel": "View:",
            "filterActive": "Filter active",
            "createdOn": "Created on:",
            "unnamedProject": "Unnamed Project",
            "noDate": "NO DATE",
            "today": "Today",
            "noData": "No data:",
            "benefitAxis": "Benefit (Low \u2192 High)",
            "strategyAxis": "Strategic Alignment (Low \u2192 High)",
            "capacityLimit": "CAPACITY UTILIZATION",
            "unnamedBucket": "Unnamed",
            "projectCount": "Projects"
        },
        "strategiebezug": "Strategic Alignment:",
        "aufwand": "Effort:",
        "nutzen": "Benefit:",
        "chartXAxis": "Strategic Alignment (Low ➔ High)",
        "chartYAxisBenefit": "Benefit (Low ➔ High)",
        "chartYAxisEffort": "Effort (Low ➔ High)",
        "chartBubbleSize": "Bubble size",
        "chartBubbleScaledBenefit": "Scaled project benefit (1-10)",
        "chartBubbleEstimatedEffort": "Estimated effort (S-XL)",
        "gesetzlicherAuftrag": "Statutory Mandate",
        "board": "Board",
        "reset": "Reset",
        "zeit": "Time",
        "budget": "Budget",
        "team": "Team",
        "qualitaet": "Quality",
        "kapazitaetsauslastung": "Capacity Utilization",
        "keineProjekteZurDarstellungGefunden": "No projects found to display.",
        "normalesProjekt": "Normal Project",
        "wipLimitUeberschritten": "WIP Limit Exceeded",
        "abbrechen": "Cancel",
        "trotzdemFortfahren": "Proceed Anyway",
        "keineAngabe": "N/A",
        "keinStatus": "No Status",
        "aufKurs": "On Track",
        "ungewiss": "Uncertain",
        "kritisch": "Critical",
        "cafBezugAlle": "CAF Reference (All)",
        "projektleitungAlle": "Project Lead (All)",
        "project": "Project",
        "projectList": "Project list",
        "moveLock": {
            "foreign": "This project cannot be moved because it is managed by {{unit}}.",
            "noRight": "Only portfolio management or the person responsible can move this project."
        },
        "relation": {
            "promotedFrom": "Forwarded by {{unit}}",
            "topic": "Topic {{topics}} · {{unit}}",
            "personal": "Participation · {{unit}}"
        }
    },
    "project": {
        "labels": "Labels",
        "noAccess": "You do not have access to this project.",
        "loadError": "Error loading the project.",
        "details": "Project Details",
        "title": "Project Name",
        "description": "Objectives & Description",
        "initialSituation": "Initial Situation",
        "managementSummary": "Executive Summary",
        "sponsor": "Client / Sponsor",
        "steeringCommittee": "Steering Committee",
        "measurableSuccess": "Measurable Success",
        "cafReference": "CAF Reference",
        "category": "Category",
        "responsible": "Project Lead",
        "startDate": "Start Date",
        "dueDate": "Target Date",
        "effortEstimate": "Estimated Effort",
        "notEstimated": "Not estimated",
        "strategicImportance": "Strategic Importance",
        "benefit": "Benefit (Internal / External)",
        "isLegalRequirement": "Statutory Mandate",
        "status": "Status",
        "decisionNeeded": "Escalation / Decision Needed",
        "decisionNeededPlaceholder": "What must the steering committee / management decide for the project to continue?",
        "addEscalation": "Add decision need",
        "saveEscalation": "Add",
        "noEscalations": "No current decision needed.",
        "activeEscalations": "Required Decisions",
        "resolveEscalationBtn": "Document decision",
        "recordDecisionTitle": "Document Decision",
        "recordDecisionIntro": "Record what was decided regarding the current escalation. This decision will be moved to history after saving.",
        "decision": "Decision made",
        "logDecisionBtn": "Log decision",
        "decisionHistory": "Decision History",
        "trafficLights": {
            "title": "Pulse Check (Traffic Lights)",
            "time": "Time / Schedule",
            "budget": "Costs / Budget",
            "resources": "Resources / Team",
            "quality": "Quality / Scope"
        },
        "deleteTitle": "Delete Project?",
        "deleteMessage": "Are you sure you want to permanently delete this project including all tasks?",
        "archiveWarningTitle": "Archive Project?",
        "archiveWarningMessage": "If you set the status to 'Archived', all measures (tasks) of this project will also be archived. Do you want to proceed?",
        "archiveWarningConfirm": "Yes, archive",
        "export": {
            "button": "Export Current View",
            "title": "Export Project as PDF",
            "subtitle": "Select the desired sections for export.",
            "optionDetails": "Project Details",
            "optionPulseCheck": "Pulse Check",
            "optionPulseCheckSummary": "Pulse Check (incl. Executive Summary)",
            "optionTaskOverview": "Measures Overview",
            "optionTaskDetails": "Measures Details",
            "optionRisks": "Project Risks",
            "optionDecisions": "Required Decisions",
            "decisionNone": "None",
            "decisionOpen": "Only open decisions",
            "decisionAll": "All decisions (incl. history)",
            "detailsTitle": "Export Project Details",
            "tasksTitle": "Export Measures",
            "cancel": "Cancel",
            "create": "Create PDF",
            "generating": "Generating..."
        },
        "report": {
            "title": "Project Report",
            "decisionHistoryTitle": "Required Decisions & History",
            "decisionOpenTitle": "Open decisions (Escalations):",
            "decisionNone": "Currently no open decisions.",
            "decisionHistorySubtitle": "Decision history (already made):",
            "decisionHistoryNone": "No history available.",
            "decisionDate": "Decided on",
            "decisionQuestion": "Question:",
            "decisionAnswer": "Decision:",
            "riskTitle": "Project Risks",
            "riskMatrixTitle": "Risk Matrix",
            "riskListTitle": "Risk List",
            "riskProb": "Probability (1 - 4)",
            "riskDamage": "Damage Impact (1 - 4)",
            "riskNo": "No.",
            "riskName": "Risk Name",
            "riskTableDamage": "Damage",
            "riskTableProb": "Prob.",
            "riskDesc": "Description"
        },
        "newProject": "New Project",
        "notFound": "Project not found"
    },
    "backend": {
        "errors": {
            "ungueltigeEingabe": "Invalid input.",
            "unerwarteterFehler": "An unexpected error occurred. Please try again later.",
            "nurTenantAdmins": "This action is reserved for tenant admins.",
            "keineBerechtigungFuerEinheit": "Not authorized for this organizational unit.",
            "organisationseinheitNichtGefunden": "Organizational unit not found.",
            "organisationseinheitFehlt": "The organizational unit is missing.",
            "organisationseinheitFehltOderDeaktiviert": "The organizational unit does not exist or is deactivated.",
            "keineOrganisationseinheitZugeordnet": "You are not assigned to an organizational unit.",
            "uebergeordneteEinheitNichtGefunden": "The parent unit was not found.",
            "uebergeordneteEinheitFehltOderDeaktiviert": "The parent unit does not exist or is deactivated.",
            "nameDerEinheitFehlt": "The name of the organizational unit is required.",
            "obersteEinheitNichtVerschiebbar": "The top-level unit cannot be moved.",
            "einheitNichtSichSelbstUnterstellt": "A unit cannot report to itself.",
            "verschiebenUnterDeaktivierteEinheit": "Units cannot be moved under a deactivated unit.",
            "zirkulaereHierarchie": "The new parent unit lies below the unit being moved. This would create a circular hierarchy.",
            "zuVieleEbenenNachVerschiebung": "After the move, the structure would have more levels than allowed.",
            "anlegenUnterDeaktivierterEinheit": "No units can be created under a deactivated unit.",
            "hoechsteEbeneErreicht": "The organizational structure already has the maximum number of levels.",
            "einfuegenHoechsteEbeneErreicht": "The structure already has the maximum number of levels; no unit can be inserted above it.",
            "mandantHatBereitsObersteEinheit": "The tenant already has a top-level unit.",
            "obersteEinheitNichtDeaktivierbar": "The top-level unit cannot be deactivated.",
            "zuerstUntereinheitenDeaktivieren": "Please deactivate all subordinate units first.",
            "verschiebenBestaetigen": "Moving the unit revokes shares or forwardings. Please confirm the consequences.",
            "keineEindeutigeObersteEinheit": "The organizational structure has no unique top-level unit. Please contact support.",
            "obersteEinheitGenauEineUntereinheit": "The top-level unit can only be removed if it has exactly one subunit.",
            "verbleibendeEinheitDeaktiviert": "The remaining unit is deactivated and cannot become the top-level unit.",
            "obersteEinheitHatPersonen": "People are assigned to the top-level unit. It can no longer be removed.",
            "obersteEinheitHatAssessments": "The top-level unit has assessments. It can no longer be removed.",
            "obersteEinheitHatFreigaben": "Assessments have already been shared with the top-level unit. It can no longer be removed.",
            "obersteEinheitHatProjekte": "The top-level unit has projects. It can no longer be removed.",
            "obersteEinheitHatWeitergaben": "Projects have already been forwarded to the top-level unit. It can no longer be removed.",
            "obersteEinheitHatQuerschnittsthema": "The top-level unit is responsible for a cross-cutting topic. It can no longer be removed.",
            "obersteEinheitNichtLoeschbar": "The top-level unit cannot be deleted.",
            "einheitHatUntereinheiten": "This organizational unit still has subordinate units.",
            "einheitHatPersonen": "People are still assigned to this organizational unit.",
            "einheitHatAssessments": "This organizational unit has assessments. Please deactivate it instead of deleting it.",
            "einheitHatProjekte": "This organizational unit has projects. Please deactivate it instead of deleting it.",
            "einheitHatQuerschnittsthema": "This organizational unit is responsible for a cross-cutting topic.",
            "projekteAnlegenNurPortfolioUndProjektleitung": "Only portfolio management and project leads can create projects.",
            "querschnittsthemenNurPortfoliomanagement": "Cross-cutting topics are assigned by the portfolio management of the originating unit.",
            "weitergebenNurPortfoliomanagement": "Only the portfolio management of the unit currently holding the project can forward it.",
            "obersteEinheitKannNichtWeitergeben": "The top-level unit cannot forward projects.",
            "zurueckziehenNurPortfoliomanagement": "Only portfolio management can withdraw a forwarding.",
            "projektNichtVonIhrerEinheitWeitergegeben": "Your unit has not forwarded this project.",
            "unbekanntesQuerschnittsthema": "Unknown or deactivated cross-cutting topic.",
            "querschnittsthemaDeaktiviert": "The cross-cutting topic is deactivated.",
            "sichtDerEinheitNurPortfoliomanagement": "The unit's view is maintained by portfolio management.",
            "projektFuerEinheitNichtFreigegeben": "The project is not shared with your unit.",
            "unbekannteKategorie": "Unknown category.",
            "unbekannterPortfoliostatus": "Unknown portfolio status.",
            "notizLeer": "The note is empty.",
            "notizNichtGefunden": "Note not found.",
            "nurEigeneNotizenLoeschen": "You can only delete your own notes.",
            "projekteLoeschenNurPortfoliomanagement": "Projects are deleted by the portfolio management of the originating unit.",
            "ungueltigeProjektdaten": "The project data is invalid. Please check your entries.",
            "titelFehlt": "The title is missing.",
            "assessmentNichtGefunden": "Assessment not found.",
            "keineBerechtigungFuerAssessmentsDerEinheit": "Not authorized for assessments of this organizational unit.",
            "assessmentAbgeschlossen": "The assessment is completed. Please reopen it first.",
            "aktionImStatusNichtMoeglich": "This action is not possible in the current status.",
            "assessmentsAnlegenNurQmManagement": "Assessments are created by QM-Management.",
            "obersteEinheitOhneUebergeordnete": "The top-level unit has no parent unit.",
            "bewertungBereitsEingereicht": "This assessment has already been submitted.",
            "aggregatNurQmUndManagement": "Only QM-Management and Management of the unit itself can see the aggregate.",
            "vergleichswerteNurQmUndManagement": "Only QM-Management and Management can see comparison values.",
            "ungueltigeFarbe": "Invalid color.",
            "zustaendigeEinheitFehlt": "The responsible unit is missing.",
            "nameFehlt": "The name is missing.",
            "nameZuLang": "The name is too long.",
            "querschnittsthemaNichtGefunden": "Cross-cutting topic not found.",
            "kategorienNurPortfoliomanagement": "Categories are maintained by the unit's portfolio management.",
            "kategorieNichtGefunden": "Category not found.",
            "mindestensEinStatus": "At least one status is required.",
            "statusIdsNichtEindeutig": "The status IDs are not unique.",
            "statusNamenDoppelt": "Two statuses have the same name.",
            "startNachFaelligkeit": "The start date must not be after the due date.",
            "erinnerungNachFaelligkeit": "The reminder date must not be after the due date.",
            "statusFehlt": "The status is missing.",
            "kommentarLeer": "The comment must not be empty.",
            "farbeFehlt": "The color is missing.",
            "risikonameFehlt": "The risk name is missing.",
            "keineBerechtigungBenutzerLoeschen": "Not authorized to delete users.",
            "superAdminsNichtLoeschbar": "Super Admins cannot be deleted.",
            "orgAdminsNurEigeneEinheitEntfernen": "Organization Admins may only remove users of their own organizational unit.",
            "orgAdminsKeineTenantAdminsLoeschen": "Organization Admins cannot delete tenant admins.",
            "spracheNichtVerfuegbar": "This language is not enabled for your tenant.",
            "keineBerechtigungRollenBearbeiten": "Not authorized to edit roles.",
            "superAdminsUndAssessorenNichtHier": "Super Admins and assessors are not managed here.",
            "orgAdminsNurEigeneEinheitRollen": "Organization Admins may only change roles in their own organizational unit.",
            "orgAdminsKeineTenantAdminsErnennen": "Organization Admins cannot appoint tenant admins.",
            "benutzerNichtImMandanten": "The user does not belong to this tenant.",
            "totpAktivierenFehlgeschlagen": "Sign-in with an authenticator app (TOTP) could not be enabled.",
            "konfigurationSpeichernFehlgeschlagen": "The configuration could not be saved.",
            "datenexportFehlgeschlagen": "The data export failed.",
            "loeschungFehlgeschlagen": "The deletion failed.",
            "einladenNurAdmins": "Only tenant admins and Organization Admins can invite users.",
            "orgAdminsNurEigeneEinheitEinladen": "Organization Admins can only invite users to their own organizational unit.",
            "assessorenEinladenNurAdmins": "Only tenant admins and Organization Admins can invite external assessors.",
            "assessorHinzufuegenFehlgeschlagen": "The assessor could not be added.",
            "keineBerechtigungAssessorenEntfernen": "Not authorized to remove assessors.",
            "assessorNichtGefunden": "Assessor not found.",
            "orgAdminsNurEigeneAssessorenEntfernen": "Organization Admins may only remove assessors of their own organizational unit.",
            "orgAdminsKeineMandantenweitenAssessoren": "Organization Admins cannot remove tenant-wide assessors.",
            "ungueltigeEmail": "Invalid email address.",
            "einladungNichtGefunden": "The invitation was not found. Please check the link.",
            "einladungBereitsVerwendet": "This invitation has already been used.",
            "einladungAbgelaufen": "The invitation has expired. Please request a new one.",
            "anzeigenameFehlt": "The display name is missing.",
            "idpEntityIdFehlt": "The IdP entity ID is missing.",
            "ssoUrlUngueltig": "The SSO URL must be a valid URL.",
            "zertifikatFehlt": "The certificate is too short or missing.",
            "ssoDomainFehlt": "At least one SSO domain is required.",
            "clientIdFehlt": "The client ID is missing.",
            "issuerUrlUngueltig": "The issuer URL must be a valid URL.",
            "ssoKonfigurationFehlgeschlagen": "Error configuring SSO.",
            "ssoEntfernenFehlgeschlagen": "Error removing the SSO configuration.",
            "oidcKonfigurationFehlgeschlagen": "Error configuring OIDC.",
            "keineSsoKonfigurationFuerDomain": "No SSO is set up for the domain of your email address.",
            "benutzerErstellungFehlgeschlagen": "The user account could not be created.",
            "sicherheitspruefungFehlgeschlagen": "The security check failed. Please reload the page.",
            "cafStructureSaveFailed": "Error saving the structure. Do you have the required permissions?",
            "authErforderlich": "Authentication required",
            "falscherMandant": "Wrong tenant.",
            "fehlerBeimLoeschenDesAuthaccounts": "Error deleting the auth account. Operation aborted to avoid inconsistent data.",
            "benutzerNichtGefunden": "User not found.",
            "zugriffVerweigertFremderMandant": "Access denied (other tenant).",
            "superAdminsKoennenNichtGeaendert": "Super Admins cannot be modified.",
            "sieHabenKeineBerechtigungDie": "You are not authorized to assign the \"Super Admin\" role.",
            "authentifizierungErforderlich": "Authentication required",
            "fehlendeDatenName": "Missing data: name.",
            "benutzerExistiertBereits": "User already exists.",
            "keinMandantZugeordnet": "No tenant assigned.",
            "bewertungKonnteNichtGeladenWerden": "The assessment could not be loaded.",
            "nurAdministratorenDuerfenAssessmentsLoeschen": "Only administrators may delete assessments.",
            "youAreNotAuthorizedFor": "You are not authorized for this client.",
            "youHaveBeenRemovedFrom": "You have been removed from this tenant.",
            "keineBerechtigungZumAbstimmen": "Not authorized to vote.",
            "nurSuperAdminsDuerfenDiese": "Only Super Admins may perform this action.",
            "zieluserIstKeinAssessor": "The target user is not an assessor.",
            "sieHabenKeineBerechtigungSuper": "You are not authorized to invite Super Admins.",
            "ungueltigeRolle": "Invalid role.",
            "benutzerExistiertBereitsImSystem": "User already exists in the system.",
            "keinMandantTenantIdGefunden": "No tenant (tenant ID) found.",
            "benutzerExistiertBereitsIstAber": "User already exists but is not an assessor",
            "mismatchTenantId": "Mismatch Tenant ID",
            "ungueltigerLink": "Invalid link.",
            "falscherLinktyp": "Wrong link type.",
            "linkAbgelaufen": "Link expired.",
            "kontoNichtGefunden": "Account not found.",
            "fehlerBeimPruefenDerEinladung": "Error checking the invitation.",
            "tokenUngueltig": "Invalid token.",
            "bereitsVerwendet": "Already used.",
            "abgelaufen": "Expired.",
            "ungueltigerInhaltNachBereinigungLeer": "Invalid content (empty after sanitization).",
            "zuVieleAnfragenBitteVersuchen": "Too many requests. Please try again later.",
            "fehlerBeimSendenDerNachricht": "Error sending the message.",
            "keineEmailImTokenGefunden": "No email found in the token.",
            "unzureichendeBerechtigungLevel20Erforderlich": "Insufficient permissions: level 20 required.",
            "projektNichtGefunden": "Project not found.",
            "level20DarfNurZugewiesene": "Level 20 may only edit assigned projects.",
            "risikoNichtGefunden": "Risk not found.",
            "level20ErforderlichUmRisiken": "Level 20+ required to delete risks.",
            "level20DarfNurEigene": "Level 20 may only edit their own projects (those they are responsible for).",
            "unzureichendeBerechtigungUmProjektmitgliederZu": "Insufficient permissions to manage project members.",
            "mandantNichtGefunden": "Tenant not found.",
            "fehlerBeiDerDomainpruefung": "Error checking the domain.",
            "keineEmailadresseImAuthtoken": "No email address in the auth token.",
            "ungueltigeEmaildomain": "Invalid email domain.",
            "zugriffVerweigertSieSindKein": "Access denied: you are not a member of this project.",
            "kommentarNichtGefunden": "Comment not found.",
            "sieDuerfenNurEigeneKommentare": "You may only delete your own comments.",
            "konfigurationsfehlerAiServiceNichtVerfuegbar": "Configuration error: AI service not available.",
            "benutzerIstNichtEingeloggt": "User is not logged in.",
            "benutzerprofilNichtGefunden": "User profile not found.",
            "multifaktorauthentifizierungMfaIstFuerDiesen": "Multi-factor authentication (MFA) is required for this tenant.",
            "mfaZuerstSelbstEinrichten": "First set up a second factor for your own account. Only then can you enforce MFA for everyone.",
            "keineBerechtigung": "Not authorized.",
            "projektleitungAendertPortfoliomanagement": "The project lead is assigned by the portfolio management of the originating unit.",
            "kiFuerMandantAusgeschaltet": "AI features are turned off for this tenant.",
            "kiAbrechnung": "The AI is unavailable because of a billing problem. Please contact support.",
            "kiModellNichtVerfuegbar": "The AI model is currently unavailable. Please contact support.",
            "kiAusgelastet": "The AI is currently busy. Please try again in a few minutes.",
            "kiNichtErreichbar": "The AI cannot be reached at the moment. Please try again later.",
            "kiKeineAntwort": "The AI did not return a usable answer. Please try again.",
            "kiTageslimitErreicht": "The daily limit for AI features has been reached. Please try again tomorrow.",
            "kiTextZuLang": "The text is too long for the AI. Please shorten it.",
            "fehlerBeiDerBerechtigungspruefung": "Error checking permissions.",
            "tenantidFehltFuerDieBerechtigungspruefung": "tenantId is missing for the permission check.",
            "zugriffAufDiesenMandantenVerweigert": "Access to this tenant denied.",
            "mandantGesperrt": "The tenant is locked. Access is not possible.",
            "mandantGesperrtKeineZugaenge": "The tenant is locked. No people or assessors can be added.",
            "mandantGesperrtKeineEinreichung": "The tenant is locked and does not accept any assessments.",
            "adminSaveFailed": "Error saving. This is often a permissions issue."
        }
    },
    "aisettings": {
        "promise": "Promise"
    },
    "riskHistory": {
        "taskCreated": "Measure created",
        "damageChanged": "Extent of Damage: {{from}} → {{to}}",
        "probabilityChanged": "Probability: {{from}} → {{to}}",
        "nameChanged": "Title changed",
        "descriptionChanged": "Description changed",
        "noChanges": "No relevant changes",
        "created": "Created"
    },
    "contentmanagement": {
        "globaleVorlagen": "Global templates",
        "meineOrganisation": "My organization",
        "typ": "Type",
        "gesperrt": "Locked",
        "zentralVerwaltetHinweis": "Note: This content is managed centrally and can only be edited by system administrators.",
        "verfuegbareMethoden": "Available methods ({{count}})",
        "id": "ID",
        "abschnittLoeschen": "Delete section",
        "beispielEntfernen": "Remove example",
        "methodenList": "List of methods",
        "modulLoeschen": "Delete module",
        "methodeLoeschen": "Delete method",
        "beispielLoeschen": "Delete example",
        "themenfeldLoeschenFrage": "Do you really want to delete the theme \"<strong>{{title}}</strong>\" and all the criteria it contains?",
        "kriteriumLoeschenFrage": "Do you really want to delete the criterion \"<strong>{{title}}</strong>\"?",
        "modulLoeschenFrage": "Do you really want to delete the information module \"<strong>{{title}}</strong>\"?",
        "abschnittLoeschenFrage": "Do you really want to delete the section \"<strong>{{title}}</strong>\"?",
        "methodeEntfernenFrage": "Do you really want to remove the method \"<strong>{{title}}</strong>\" from the toolbox?",
        "beispielEntfernenFrage": "Do you really want to remove the example \"<strong>{{title}}</strong>\"?",
        "ok": "OK",
        "promise": "Promise",
        "inhalteExportieren": "Export Content",
        "cafThemenfelderKriterien": "CAF Themes & Criteria",
        "informationsbereiche": "Information Sections",
        "methodenbaukasten": "Method Toolbox",
        "standardIndividualisierung": "Standard & Customization",
        "inhalteMitEinemSchlossSymbol": "Contents with a lock symbol (",
        "standardContentInfo": "Contents marked with a lock icon are part of the global CAF standard and cannot be edited. You can however add your own themes, criteria, and examples.",
        "beschreibung": "Description",
        "detailbeschreibung": "Detailed Description",
        "beispiele": "Examples",
        "beispielHinzufuegen": "Add Example",
        "kriteriumHinzufuegen": "Add Criterion",
        "neuesThemenfeldHinzufuegen": "Add New Theme",
        "ueberInformationsmodule": "About Information Modules",
        "dieseModuleWerdenImLern": "These modules are displayed in the Learning Hub above the themes. They provide general information about CAF.",
        "modulTitel": "Module Title",
        "einleitungBeschreibung": "Introduction / Description",
        "abschnittHinzufuegen": "Add Section",
        "neuesInformationsmodulHinzufuegen": "Add New Information Module",
        "zentraleSammlungVonMethodenDie": "Central collection of methods linked to CAF criteria. These are visible to all tenants.",
        "methodeHinzufuegen": "Add Method",
        "titel": "Title",
        "zielZweck": "Goal / Purpose",
        "aktionen": "Actions",
        "keineMethodenVorhanden": "No methods available.",
        "achtung": "ATTENTION:",
        "diesEntferntAlleIhreIndividuellen": "This removes all your individual customizations. Only the standard CAF model content will remain. This cannot be undone.",
        "themenfeldLoeschen": "Delete Theme",
        "kriteriumLoeschen": "Delete Criterion",
        "methodenDurchsuchen": "Search methods...",
        "themenfeldWaehlen": "Choose theme...",
        "kriteriumWaehlen": "Choose criterion...",
        "bearbeiten": "Edit",
        "loeschen": "Delete",
        "themenfeld": "Theme",
        "kriterium": "Criterion",
        "beispiel": "Example",
        "abschnitt": "Section",
        "inhalt": "Content"
    },
    "mfasetup": {
        "qrCodeAlt": "QR code for the authenticator app",
        "seiteNeuLadenWennBestaetigt": "Reload page (when confirmed)",
        "oeffnenSieIhreAuthenticatorApp": "Open your Authenticator App (Google Authenticator, Microsoft Authenticator, etc.) and scan this code.",
        "gebenSieDen6Stelligen": "Enter the 6-digit code",
        "ihrKontoIstNunZusaetzlich": "Your account is now additionally protected. You will be redirected shortly.",
        "zumDashboard": "To Dashboard"
    },
    "mytasks": {
        "keineAufgabenEntsprechenDenGewaehlten": "No tasks match the selected filters."
    },
    "projectdetail": {
        "projektbewertungPriorisierung": "Project Assessment & Prioritization",
        "ungueltigeVerantwortlichkeit": "Invalid Responsibility",
        "dieAktuellZugewiesenePersonIst": "The currently assigned person is no longer a user of this tenant. Please assign a new project lead.",
        "statusSchnelleinschaetzung": "Pulse Check Status",
        "entschieden": "DECIDED",
        "urspruenglichesProblem": "Original Question:",
        "projektEinstellungen": "Project Settings",
        "exportieren": "Export project details",
        "tabs": "Tabs",
        "bitteWaehlen": "Select...",
        "kurzerStatusbericht": "Brief management summary...",
        "loeschen": "Delete",
        "wasWurdeEntschieden": "What was decided...?",
        "tasksTab": "Measures (Tasks)",
        "nichtGeschaetzt": "Not estimated",
        "foreignProjectNote": "This project belongs to {{unit}}. You can see its project data and risks but cannot edit it.",
        "sharingTab": "Sharing",
        "evaluationByPortfolio": "Rated by portfolio management",
        "evaluationOfOrigin": "Rating of the originating unit"
    },
    "superadmin": {
        "ausstehend": "Pending",
        "ausstehendHinweis": "Invited, registration not yet completed",
        "mandantGeloescht": "Tenant “{{name}}” has been permanently deleted.",
        "fehlerLadenMandanten": "Error loading tenants.",
        "fehlerMandantAnlegen": "An error occurred while creating the tenant. Please check your entries.",
        "zugriffVerweigert": "Access denied.",
        "fehlerLadenBenutzer": "Error loading users.",
        "fehlerEinladen": "Error inviting the user: {{msg}}",
        "rolleNichtGeaendert": "The role could not be changed.",
        "tenantAdminErnannt": "User \"{{email}}\" has been appointed tenant admin.",
        "ich": "ME",
        "backupHinweis": "Export and import the global templates (criteria, methods, information) here. Use this to transfer data from the production system to the test system.",
        "backupHerunterladen": "Download backup (export)",
        "backupHochladen": "Upload backup (import)",
        "erstelle": "Creating...",
        "organisationenAnzahl": "Organizations ({{count}})",
        "sperren": "Lock",
        "aktivieren": "Activate",
        "gesperrt": "Locked",
        "registrierteAssessoren": "Registered assessors ({{count}})",
        "verwaltungMandant": "Management: {{name}}",
        "rolleAdmin": "Admin",
        "alsTenantAdminBenennen": "Appoint as tenant admin",
        "emailPlatzhalterAdmin": "admin@company.com",
        "emailPlatzhalterNeu": "new@company.com",
        "benutzerExistiertText": "The user with the email address <strong>{{email}}</strong> is already registered in the system (possibly with a different role or in another tenant).",
        "loesungText": "<strong>Solution:</strong> Please use the \"Edit\" function in the list to adjust roles instead of sending a new invitation.",
        "benutzerEntfernenFrage": "Do you really want to remove the user <strong>{{email}}</strong> from this tenant?",
        "mandantLoeschenTitel": "WARNING: Delete tenant \"{{name}}\"?",
        "assessorLoeschenTitel": "WARNING: Delete assessor \"{{email}}\"?",
        "mandantLoeschenText": "This action irrevocably deletes the tenant <strong>including all data</strong> (assessments, measures, users, logs).",
        "alleBenutzerLoginsGeloescht": "All user logins will be deleted.",
        "alleBenutzerLoginsGeloeschtAnzahl": "All {{count}} user logins will be deleted.",
        "fremdbewertungenGeloeschtMarkiert": "External assessments held by external assessors will be marked as \"DELETED\".",
        "ok": "OK",
        "fehler": "Error",
        "systemAdministratoren": "System Administrators",
        "datenManagementGlobal": "Data Management (Global)",
        "erfolg": "Success",
        "nameAdmin": "Admin Name",
        "keineMandantenGefunden": "No tenants found.",
        "externeBeraterOhneEigeneOrganisation": "External consultants without their own organization",
        "keineExternenAssessorenGefunden": "No external assessors found.",
        "nameEMail": "Name / E-Mail",
        "firma": "Company",
        "status": "Status",
        "aktionen": "Actions",
        "benutzerHinzufuegen": "Add User",
        "eMail": "E-Mail",
        "name": "Name",
        "rolle": "Role",
        "einladungErfolgreich": "Invitation successful!",
        "link": "Link:",
        "linkKopieren": "Copy link",
        "vorhandeneBenutzer": "Existing users",
        "ladeBenutzer": "Loading users...",
        "keineBenutzerGefunden": "No users found.",
        "user": "User",
        "benutzerBearbeiten": "Edit user",
        "benutzer": "User",
        "abbrechen": "Cancel",
        "benutzerExistiertBereits": "User already exists",
        "derBenutzerMitDerE": "The user with the e-mail address",
        "loesung": "Solution:",
        "verstanden": "Understood",
        "moechtenSieDenBenutzer": "Do you want to delete the user",
        "dieseAktionLoeschtDenMandanten": "This action deletes the tenant",
        "alleErstelltenAssessmentsGehenVerloren": "All created assessments will be lost.",
        "diesKannNichtRueckgaengigGemacht": "This cannot be undone.",
        "dieseAktionLoeschtDasAssessor": "This action permanently deletes the assessor account and personal workspace.",
        "derLoginWirdUngueltig": "The login will become invalid.",
        "lokalGespeicherteEntwuerfeWerdenGeloescht": "Locally stored drafts will be deleted.",
        "derAssessorWirdAusAllen": "The assessor will be removed from all tenant lists.",
        "musterfirmaAg": "Sample Company Ltd.",
        "maxMustermann": "John Doe",
        "suchen": "Search...",
        "benutzerVerwalten": "Manage Users",
        "mandantUnwiderruflichLoeschenDangerZone": "Permanently delete tenant (Danger Zone)",
        "assessorUnwiderruflichLoeschenDangerZone": "Permanently delete assessor (Danger Zone)",
        "bearbeiten": "Edit",
        "loeschen": "Delete",
        "benutzerLoeschen": "Delete user",
        "tenantList": "Tenant list",
        "assessorList": "Assessor list",
        "userList": "User list",
        "roleMember": "Member of the tenant (functional roles are assigned by the Tenant admin)",
        "roleTenantAdmin": "Tenant admin (structure, people, settings)"
    },
    "usermanagement": {
        "einladungAusstehend": "Invitation pending",
        "ladenFehlgeschlagen": "Loading failed",
        "externerAssessor": "External Assessor",
        "schliessen": "Close",
        "benutzerExistiertBereits": "User already exists",
        "derBenutzerMitDerE": "The user with the e-mail address",
        "tipp": "Tip:",
        "verstanden": "Understood",
        "assessorNichtGefunden": "Assessor not found",
        "wirKonntenKeinenRegistriertenAssessor": "We could not find a registered assessor with the e-mail address",
        "hinweis": "Note:",
        "benutzerBearbeiten": "Edit user",
        "zugriffEntfernen": "Remove access",
        "ausMandantEntfernen": "Remove from tenant",
        "dieEmail": "The e-mail address",
        "esWurdeKeinAssessorMitDerEmail": "No registered assessor with the e-mail address"
    },
    "richTextEditor": {
        "bold": "Bold",
        "italic": "Italic",
        "bulletList": "Bulleted list"
    },
    "kanbanboard": {
        "wipLimitReachedIntro": "The configured WIP limit for the \"{{column}}\" column has already been reached in the following capacity buckets:",
        "wipLimitBottleneckHint": "Experience shows that moving additional measures leads to bottlenecks and delays in ongoing tasks.",
        "wipLimitConfirmQuestion": "Do you want to start work on this measure <strong>anyway</strong> due to urgent priorities?",
        "promise": "Promise",
        "kapazitaetsauslastung": "Capacity Utilization",
        "ungueltigeVerantwortlichkeit": "Invalid Responsibility",
        "wipLimitUeberschritten": "WIP Limit Exceeded",
        "abbrechen": "Cancel",
        "trotzdemFortfahren": "Proceed Anyway",
        "dieserBenutzerIstNichtTeil": "This user is not part of the project team"
    },
    "projectcategoryselect": {
        "ownGroup": "Own categories",
        "topicGroup": "Cross-cutting topics",
        "ownerUnknown": "responsible unit",
        "topicLocked": "Cross-cutting topics are assigned by the portfolio management of the originating unit.",
        "visibleFor": "visible to {{unit}}",
        "sharedWith": "Visible through cross-cutting topics to: {{units}} (project data and risks, without measures)",
        "keineKategorie": "No Category",
        "keineKategorienGefunden": "No categories found",
        "searchCategory": "Search Category..."
    },
    "projectriskstab": {
        "schaden": "Damage:",
        "wahrsch": "Prob.:",
        "score": "Score:"
    },
    "projectroadmap": {
        "unbenannt": "Untitled",
        "meilenstein": "Milestone: {{title}}",
        "keineProjekteFuerRoadMap": "No projects found for Road Map.",
        "sortierenNach": "Sort by:",
        "ohneKategorie": "No Category",
        "ohneDatum": "No Date",
        "showCategories": "Show Categories",
        "hideCategories": "Hide Categories",
        "sortAlphabetical": "Alphabetical",
        "sortStartDate": "Start Date",
        "sortEndDate": "Target Date"
    },
    "projectsettings": {
        "sie": "You",
        "feldnamePlaceholder": "e.g. Priority, Costs...",
        "bucketNamePlaceholder": "e.g. Large projects",
        "wipLimitUmschalten": "Turn WIP limit on or off",
        "entfernenHinweis": "If you remove this user, the measures stay assigned to them for now, but the user is highlighted as \"Invalid Responsibility\". The measures should then be assigned to another project member.",
        "promise": "Promise",
        "projektEinstellungen": "Project Settings",
        "mitglieder": "Members",
        "labels": "Labels",
        "projektMitglieder": "Project Members",
        "verantwortlich": "Responsible",
        "projektLabels": "Project Labels",
        "definierenSieProjektweiteLabelsDie": "Define project-wide labels that can be assigned to tasks for better structuring.",
        "neuesLabelErstellen": "Create New Label",
        "hinzufuegen": "Add",
        "verfuegbareLabels": "Available Labels",
        "labelsWerdenGeladen": "Loading labels...",
        "keineLabelsDefiniert": "No labels defined.",
        "pflicht": "Required",
        "keineBucketsDefiniert": "No buckets defined.",
        "bucketName": "Bucket Name",
        "aufwandgroessenTShirt": "Effort Sizes (T-Shirt)",
        "wipLimit": "WIP Limit",
        "achtungOffeneMassnahmen": "Attention: Open Measures",
        "dieserBenutzerHatNoch": "This user still has",
        "moechtenSieDenBenutzerTrotzdem": "Do you still want to remove the user?",
        "abbrechen": "Cancel",
        "trotzdemEntfernen": "Remove Anyway",
        "labelNameZBBug": "Label Name (e.g. Bug, Feature, Important)",
        "labelFarbeWaehlen": "Choose Label Color",
        "labelLoeschen": "Delete Label",
        "feldAktivieren": "Enable Field",
        "pflichtfeld": "Required Field",
        "feldEntfernen": "Remove Field",
        "loeschen": "Delete",
        "zugewiesen": "assigned."
    },
    "projecttaskmanagement": {
        "neueAufgabe": "New task",
        "aufgabeHinzufuegen": "+ Add task",
        "promise": "Promise",
        "timelineGanttAnsicht": "Timeline / Gantt View",
        "dieseAnsichtWirdInEinem": "This view will be expanded in a future update. It will allow scheduling tasks graphically on a timeline and visualizing dependencies.",
        "liste": "List",
        "board": "Board",
        "timeline": "Timeline"
    },
    "risk": {
        "promise": "Promise"
    },
    "taskdetail": {
        "commentsLoadError": "Error loading the comments.",
        "commentDeleteError": "The comment could not be deleted.",
        "unknownUser": "Unknown person",
        "allProjectMembers": "All project members",
        "kommentarWirklichLoeschen": "Do you really want to delete this comment?",
        "keineAuswahl": "– No selection –",
        "status": "Status",
        "promise": "Promise",
        "ungueltigeVerantwortlichkeit": "Invalid Responsibility",
        "dieAktuellZugewiesenePersonIst": "The currently assigned person is not part of the project team. Please assign the measure to a valid project member.",
        "beschreibung": "Description",
        "checkliste": "Checklist",
        "verantwortlich": "Responsible",
        "startdatum": "Start Date",
        "faelligkeitsdatum": "Due Date",
        "erinnerungsdatum": "Reminder Date",
        "massnahmengruppe": "Measure Group",
        "keineGruppe": "No Group",
        "kommentareVerlauf": "Comments & History",
        "ladeKommentare": "Loading comments...",
        "keineKommentareVorhanden": "No comments available.",
        "worumGehtEs": "What is this about?",
        "aufDemKanbanBoardAnzeigen": "Show on Kanban Board",
        "detaillierteBeschreibungDerAufgabe": "Detailed description of the task...",
        "neuerEintrag": "New Entry...",
        "loeschen": "Delete",
        "neueAufgabeErstellen": "Create New Task",
        "aufgabeBearbeiten": "Edit Task",
        "titelDerAufgabe": "Task Title",
        "schliessen": "Close",
        "abbrechen": "Cancel",
        "wirdGespeichert": "Saving...",
        "aufgabeSpeichern": "Save Task",
        "neuenKommentarSchreiben": "Write a new comment... (type @ for name)",
        "sendError": "Error sending the comment",
        "nichtGeschaetzt": "Not estimated",
        "sKlein": "S (Small)",
        "mMittel": "M (Medium)",
        "lGross": "L (Large)",
        "xlSehrGross": "XL (Very large)",
        "checkliste.eintragErledigt": "Mark item as done",
        "checkliste.eintragTitel": "Checklist item title",
        "checkliste.entfernen": "Remove checklist item"
    },
    "tasklabelselect": {
        "wirdErstellt": "Creating...",
        "labelErstellen": "Create label \"{{name}}\"",
        "farbeAuswaehlen": "Select color {{color}}",
        "promise": "Promise",
        "keineLabelsGefunden": "No labels found.",
        "neuesLabelErstellen": "Create New Label",
        "labelsAuswaehlen": "Select Labels..."
    },
    "tasklist": {
        "unbenannteMassnahme": "Untitled measure",
        "promise": "Promise",
        "massnahmenTasks": "Measures (Tasks)",
        "nochKeineMassnahmenDefiniert": "No measures defined yet.",
        "nichtZugewiesen": "Unassigned",
        "ungueltig": "Invalid Responsibility",
        "dieserBenutzerIstNichtTeil": "This user is not part of the project team",
        "loeschen": "Delete",
        "columns": {
            "title": "Measure",
            "status": "Status",
            "dueDate": "Target Date",
            "assignee": "Responsible",
            "checkbox": "Status",
            "actions": "Actions"
        },
        "toggleStatus": "Toggle status"
    },
    "taskroadmap": {
        "verschieben": "Change order",
        "meilenstein": "Milestone: {{title}}",
        "keineMassnahmenFuerZeitleisteGefunden": "No measures found for timeline.",
        "sortierenNach": "Sort by:",
        "ohneLabel": "No Label",
        "ohneDatum": "No Date",
        "ohneGruppe": "No Group",
        "gruppierenNach": "Group by:",
        "keine": "None",
        "massnahmengruppe": "Measure Group",
        "hideGroups": "Hide Groups",
        "showGroups": "Show Groups",
        "hideLabels": "Hide Labels",
        "showLabels": "Show Labels",
        "sortAlphabetical": "Alphabetical",
        "sortStartDate": "Start Date",
        "sortEndDate": "End Date"
    },
    "assessmentchart": {
        "punkte": "Points",
        "keineDatenVerfuegbar": "No data available."
    },
    "cafinteractivemodel": {
        "befaehiger": "Enablers",
        "ergebnisse": "Results"
    },
    "createproject": {
        "promise": "Promise",
        "neuesProjektErstellen": "Create New Project",
        "aiTitelVorschlagen": "Suggest AI Title",
        "projektbewertungPriorisierung": "Project Assessment & Prioritization",
        "gesetzlicherAuftrag": "Statutory Mandate",
        "istDiesesProjektZwingendUmzusetzen": "Is this project mandatory to implement?",
        "abbrechen": "Cancel",
        "bitteWaehlen": "Please select...",
        "wirdErstellt": "Creating...",
        "projektErstellen": "Create Project"
    },
    "dangerconfirmation": {
        "zurBestaetigungTippenSieBitte": "To confirm, please type",
        "grossUndKleinschreibungMussBeachten": "Case sensitivity must be observed.",
        "abbrechen": "Cancel",
        "loeschen": "Deleting...",
        "endgueltigLoeschen": "Delete permanently"
    },
    "deleteconfirmation": {
        "loeschen": "Deleting..."
    },
    "errorboundary": {
        "error": "Error:"
    },
    "mfaverification": {
        "promise": "Promise",
        "close": "Close",
        "code": "Code"
    },
    "notificationbell": {
        "benachrichtigungen": "Notifications",
        "alleAlsGelesenMarkieren": "Mark all as read",
        "keineBenachrichtigungenVorhanden": "No notifications available."
    },
    "notifications": {
        "reminder": "Reminder",
        "mention": "New Mention",
        "reorganization": "Unit moved"
    },
    "permissionselection": {
        "berechtigungsLevelAuswaehlen": "Select Role & Permissions",
        "waehlenSieDieZutreffendeRolle": "Select the functional role for this user within their organizational unit.",
        "roleSelectionTitle": "Select Role & Permissions",
        "roleSelectionSubtitle": "Select the functional role for this user within their organizational unit.",
        "roles": {
            "qm_management": {
                "title": "QM-Management",
                "description": "Creates, duplicates, deletes, and publishes CAF assessments. Performs strategic project prioritization and governs information sharing with higher tiers.",
                "descriptionV2": "Responsible for the unit's CAF self-assessment: creates and completes it and shares it with the parent unit. Sees the assessments shared by subordinate units."
            },
            "management": {
                "title": "Management",
                "description": "Full visibility into all projects within the unit. Actively participates in current CAF assessments (enters scores, strengths, and potentials).",
                "descriptionV2": "Head of the unit. Contributes to the CAF assessment and has read access to all CAF and portfolio data of the unit."
            },
            "project_management": {
                "title": "Project lead",
                "description": "Operational leadership of own projects. Manages project details, milestones, risks, traffic lights, and tasks.",
                "descriptionV2": "Creates projects and leads own projects (details, risks, measures)."
            },
            "project_team": {
                "title": "Project team",
                "description": "Executes assigned tasks and measures (updates status, checks off checklists, writes comments).",
                "descriptionV2": "Works on assigned measures in projects the person contributes to."
            },
            "portfolio_management": {
                "title": "Portfolio management",
                "description": "Manages the unit's project portfolio: prioritizes and evaluates projects, forwards them to the parent unit and assigns cross-cutting topics."
            }
        },
        "addons": {
            "isOrgAdmin": {
                "title": "Org-Admin",
                "description": "Authorizes the user to invite and manage colleagues within their own organizational unit."
            },
            "isTenantAdmin": {
                "title": "Tenant-Admin",
                "description": "Manages the N-level organizational structure, global settings, and appoints Org-Admins.",
                "descriptionV2": "Manages the organizational structure, people, settings and cross-cutting topics. Cannot see the content of other units."
            }
        },
        "levels": {
            "50": {
                "title": "Tenant-Admin",
                "description": "Manages organizational structure, global settings, and appoints Org-Admins."
            },
            "40": {
                "title": "QM-Management",
                "description": "Create, publish, and delete CAF assessments. Strategic project prioritization."
            },
            "30": {
                "title": "Management",
                "description": "Active participation in CAF assessments and full visibility into all projects of the unit."
            },
            "20": {
                "title": "Project lead",
                "description": "Lead own projects, risks, and tasks."
            },
            "10": {
                "title": "Project team",
                "description": "Work on assigned tasks and measures."
            }
        },
        "rolesSelectionTitle": "Select functional roles",
        "rolesSelectionSubtitle": "The roles apply within the person's organizational unit. Multiple roles are possible.",
        "apply": "Apply",
        "rolesTitle": "Functional roles"
    },
    "principlesbubbles": {
        "grundsaetzeDerExzellenz": "Principles of Excellence"
    },
    "screenshotcarousel": {
        "bildNummer": "Image {{number}}: {{title}}",
        "bildWirdSpaeterEingefuegt": "Image will be inserted later",
        "vorherigesBild": "Previous Image",
        "naechstesBild": "Next Image"
    },
    "ssoconfigsection": {
        "fehlerKonfiguration": "Error configuring SSO.",
        "fehlerEntfernen": "Error removing the SSO configuration.",
        "clientIdPlaceholder": "e.g., 12345678-abcd-1234-abcd-1234567890ab",
        "issuerUrlPlaceholder": "e.g., https://login.microsoftonline.com/{tenant-id}/v2.0",
        "saml20": "SAML 2.0",
        "openidConnectOidc": "OpenID Connect (OIDC)",
        "clientId": "Client ID",
        "issuerUrl": "Issuer URL"
    },
    "assessmentwizard": {
        "durchschnitt": "Average"
    },
    "clientselection": {
        "neuenBewertungErstellen": "Create New Assessment",
        "bitteWaehlenSieDenMandanten": "Please select the tenant for which you want to create this assessment.",
        "keineAktivenMandantenGefunden": "No active tenants found.",
        "abbrechen": "Cancel"
    },
    "editremark": {
        "bemerkungBearbeiten": "Edit Remark",
        "text": "Text",
        "urlOptional": "URL (Optional)",
        "promise": "Promise"
    },
    "exportcontent": {
        "pdfErstellen": "Create PDF",
        "generiere": "Generating...",
        "inhalteAlsPdfExportieren": "Export Content as PDF",
        "waehlenSieDieZuExportierenden": "Select the content to export.",
        "methodenbaukasten": "Method Toolbox",
        "themenfelderKriterien": "Themes & Criteria",
        "informationsbereiche": "Information Sections",
        "abbrechen": "Cancel"
    },
    "methodeditor": {
        "titelPlatzhalter": "e.g., mission statement workshop",
        "bitteWaehlen": "Please select...",
        "bildvorschau": "Image preview",
        "formatHinweis": "Use line breaks for structure and <strong>**double asterisks**</strong> for bold text.",
        "titel": "Title",
        "zugehoerigesKriterium": "Linked Criterion",
        "zielUndZweck": "Goal and Purpose",
        "beschreibungDetails": "Description / Details",
        "bildOptional": "Image (Optional)",
        "bildEntfernen": "Remove Image",
        "tippsHandlungsanleitung": "Tips & Instructions",
        "nutzenSieZeilenumbruecheFuerStrukturierung": "Use line breaks for structuring and",
        "abbrechen": "Cancel",
        "kurzeBeschreibungDesZiels": "Brief description of the goal...",
        "ausfuehrlicheBeschreibung": "Detailed description...",
        "fettMarkieren": "Bold",
        "schrittFuerSchrittAnleitung": "Step-by-step instructions...",
        "methodeBearbeiten": "Edit Method",
        "neueMethodeErstellen": "Create New Method",
        "laedt": "Uploading...",
        "speichern": "Save"
    },
    "tenantUsers": {
        "bearbeiten": "Edit",
        "benutzerBearbeiten": "Edit user"
    },
    "export": {
        "title": "Export Report",
        "question": "Which content sections should be included?",
        "overview": "Score Overview",
        "overviewDesc": "Summary of scores per theme with radar chart.",
        "trendAnalysis": "Long-term Trend Analysis",
        "trendAnalysisDesc": "Development of scores across multiple assessments.",
        "chartLoading": "Loading chart...",
        "generating": "Generating..."
    },
    "languageUnavailableModal": {
        "title": "Language no longer available",
        "message": "The language you selected has been disabled by your administrator. Please choose one of the available languages to continue.",
        "selectPrompt": "Available languages:",
        "saving": "Saving..."
    },

    "legal": {
        "impressum": "Imprint / Legal Notice",
        "datenschutz": "Privacy Policy",
        "nutzungsbedingungen": "Terms of Service",
        "cookieRichtlinie": "Cookie Policy",
        "backToLogin": "Back to Login",
        "termsTitle": "Terms of Service",
        "termsGeneralTitle": "1. Scope",
        "termsGeneralContent": "These Terms of Service govern the access and use of the CAF Suite (hereinafter \"Application\"). The Application is provided exclusively for business customers (B2B).",
        "termsContractTitle": "2. Supremacy of the SaaS Agreement",
        "termsContractContent": "The detailed rights, obligations, liability issues, and data protection agreements (including Data Processing Agreements) are primarily governed by the individual Software-as-a-Service (SaaS) contract concluded with your organization. In the event of any open legal questions or contradictions, the written provisions of this main contract shall always prevail over these framework conditions.",
        "termsAccessTitle": "3. Authorized Access",
        "termsAccessContent": "Access to this instance is only permitted to explicitly authorized employees of licensed organizations and their commissioned assessors. Access credentials may under no circumstances be shared with third parties.",
        "cookiePolicyTitle": "Cookie Policy",
        "cookieNoTrackingTitle": "1. Principle: No Tracking",
        "cookieNoTrackingContent": "We fully respect your privacy and the sensitive data in this application. Therefore, we do not use any marketing, analysis, or tracking cookies (e.g., Google Analytics) in the CAF Suite. We exclusively use mechanisms that are strictly technically necessary to ensure core functions and system security.",
        "cookieAuthTitle": "2. Authentication & Session Management",
        "cookieAuthContent": "For secure login and maintaining your active session, encrypted session tokens (JSON Web Tokens) are stored in your browser (LocalStorage / IndexedDB). These tokens are security-relevant and strictly technically necessary so that your login does not immediately expire during use.",
        "cookieCloudflareTitle": "3. Cloudflare Turnstile (Bot Prevention)",
        "cookieCloudflareContent": "To protect the sensitive login screens from automated brute-force attacks, we use Cloudflare Turnstile. This service analyzes purely technical browser metadata in the background but fully refrains from cross-device user tracking and decidedly does not set any tracking cookies."
    },
    "cookies": {
        "bannerText": "We exclusively use strictly necessary cookies and storage mechanisms (e.g., for login sessions) to ensure the security and functionality of this platform. No tracking or marketing cookies are used.",
        "bannerTitle": "Cookie Notice",
        "accept": "Understood",
        "policyLink": "Learn more"
    },
    "localizedFields": {
        "language": "Language",
        "hint": "Fill in at least one language. Where a language is missing, the text of the first completed language is shown."
    },
    "tenantLock": {
        "unlock": "Unlock",
        "lockDialog": {
            "title": "Lock tenant \"{{name}}\"?",
            "intro": "Locked means: no access for the people of this tenant.",
            "signOut": "All people of the tenant are signed out and can no longer sign in.",
            "assessors": "External assessors can no longer submit assessments to this tenant.",
            "noInvites": "No more people or assessors can be added.",
            "dataKept": "The data is kept. You can still export it.",
            "reversible": "Unlocking restores access.",
            "confirm": "Lock tenant"
        },
        "unlockDialog": {
            "title": "Unlock tenant \"{{name}}\"?",
            "message": "The people of the tenant can sign in again and regain their previous access. Accounts that were already deactivated before the lock remain deactivated.",
            "confirm": "Unlock tenant"
        },
        "result": {
            "lockedTitle": "Tenant \"{{name}}\" locked",
            "unlockedTitle": "Tenant \"{{name}}\" unlocked",
            "unlockIncompleteTitle": "Tenant \"{{name}}\" remains locked",
            "people": "People of the tenant: {{n}}",
            "disabled": "Sign-in accounts deactivated: {{n}}",
            "enabled": "Sign-in accounts reactivated: {{n}}",
            "skipped": "Skipped without a sign-in account: {{n}}",
            "lockErrors": "Accounts that could not be deactivated: {{n}}. These people still have no access to data. See Cloud Logging for details.",
            "unlockErrors": "Accounts that could not be reactivated: {{n}}. The tenant remains locked. Please unlock it again; see Cloud Logging for details."
        },
        "export": {
            "action": "Export data",
            "running": "Exporting data …",
            "failedTitle": "Export failed",
            "noLink": "The export did not return a download link. Please try again."
        },
        "page": {
            "title": "Tenant locked",
            "message": "Your tenant is locked. You no longer have access to the CAF Suite; your data is kept. Please contact your organization or support.",
            "logout": "Sign out"
        },
        "loginDisabled": "Your account is deactivated, for example because your tenant is locked. Please contact your organization or support."
    },
    "orgTree": {
        "title": "Organizational Structure",
        "subtitle": "Hierarchical management of all organizational units (N-Level)",
        "addChildUnit": "Add Subordinate Unit",
        "unitName": "Unit Name",
        "unitCode": "Abbreviation",
        "headcountFTE": "Full-Time Equivalents (FTE)",
        "portfolioManagers": "Project Portfolio Managers (PPM)",
        "unitLeads": "Unit Leadership",
        "reparentSubtree": "Move Unit Subtree",
        "newParent": "New parent unit",
        "confirmDelete": "Delete organizational unit?",
        "deleteWarning": "No subordinate units or active projects may be attached.",
        "fetchError": "Error loading organizational structure.",
        "breadcrumbAriaLabel": "Organizational Path",
        "assignedOrgUnit": "Assigned Organizational Unit",
        "selectOrgUnit": "Select organizational unit...",
        "sharingScope": "Visibility / Sharing Scope",
        "sharingScopeUpward": "Incl. higher hierarchy tiers (CAF Rollup)",
        "sharingScopeTenant": "Tenant-wide visible",
        "sharingScopeParentOnly": "Direct parent tier only",
        "sharingScopeConfidential": "Confidential (This unit only)",
        "noOrgUnitAssigned": "No unit assigned (Tenant-wide)",
        "createdAnnounce": "Organizational unit created successfully.",
        "createError": "Error creating.",
        "updatedAnnounce": "Organizational unit updated successfully.",
        "updateError": "Error updating.",
        "reparentedAnnounce": "Organizational unit moved successfully.",
        "reparentError": "Error moving.",
        "reactivatedAnnounce": "Organizational unit reactivated.",
        "deactivatedAnnounce": "Organizational unit deactivated.",
        "deletedAnnounce": "Organizational unit deleted.",
        "deleteError": "Error deleting.",
        "inactive": "deactivated",
        "reactivate": "Reactivate",
        "deactivate": "Deactivate",
        "deleteHint": "Delete (only without sub-units, people and data)",
        "subtitleLevels": "Hierarchy of organizational units, up to {{max}} levels.",
        "noUnitsFound": "No organizational units created yet.",
        "createChildUnder": "New unit under \"{{parentName}}\"",
        "editUnitTitle": "Edit organizational unit",
        "unitCodePlaceholder": "e.g. AFI",
        "unitDescription": "Description",
        "reparentHelp": "Select the new parent unit for \"{{unitName}}\". All sub-units are moved along with it.",
        "confirmReparent": "Move",
        "addRootUnitV2": "Create top-level unit",
        "missingLanguages": "{{languages}} missing",
        "insertRoot": "Insert parent unit",
        "insertRootHint": "Insert a new top-level unit above this one, e.g. when a department becomes part of the whole canton",
        "insertRootFull": "The structure already has {{max}} levels.",
        "removeRoot": "Remove top-level unit",
        "confirmRemoveRoot": "Remove “{{root}}”? “{{child}}” becomes the top-level unit again and gives the tenant its name. This is only possible as long as nobody is assigned to “{{root}}” and nothing has been shared with or forwarded to it.",
        "rootInsertedAnnounce": "Parent unit inserted.",
        "insertRootError": "The unit could not be inserted.",
        "rootRemovedAnnounce": "Top-level unit removed.",
        "removeRootError": "The top-level unit could not be removed.",
        "previewError": "The consequences could not be determined.",
        "previewLoading": "Determining consequences …",
        "noConsequences": "No shares and no forwardings are revoked.",
        "subunitsMoved_one": "1 subunit is moved along.",
        "subunitsMoved_other": "{{count}} subunits are moved along.",
        "irreversibleTitle": "The following cannot be undone:",
        "releasesWithdrawn_one": "1 assessment shared with “{{parent}}” ({{years}}) is withdrawn. “{{parent}}” no longer sees it, not even in the analyses of earlier years.",
        "releasesWithdrawn_other": "{{count}} assessments shared with “{{parent}}” ({{years}}) are withdrawn. “{{parent}}” no longer sees them, not even in the analyses of earlier years.",
        "consequencesKept": "Kept: all data of the unit, cross-cutting topics and shares within the moved part. The unit can share its assessments and forward its projects to the new parent unit again; its quality management and portfolio management are notified.",
        "confirmConsequences": "I understand the consequences.",
        "insertRootIntro": "The new unit is inserted above “{{root}}”, for example when a department becomes part of the whole canton.",
        "new": "new",
        "subunitsCount_one": "1 subunit as before",
        "subunitsCount_other": "{{count}} subunits as before",
        "insertRootNothingRevoked": "No shares and no forwardings are revoked. All units keep their people, assessments and projects.",
        "insertRootReleaseLater": "Afterwards, “{{root}}” can share assessments and forward projects to “{{name}}”.",
        "insertRootLevels": "The structure then has {{levels}} levels (at most {{max}}).",
        "insertRootTenantName": "The tenant is then called “{{name}}”. The name of the top-level unit is always also the name of the tenant.",
        "insertRootAdmins": "Tenant admin rights apply to the whole tenant, so from now on to the whole new organization: all people, the structure and the full data export.",
        "insertRootAdminsToday": "Currently they are held by: {{names}}.",
        "insertRootAdminsCheck": "Afterwards, check in user management who should hold them.",
        "insertRootPeople": "People stay in their unit. Nobody is assigned to the new top-level unit yet.",
        "insertRootOperator": "The operator of CAF Suite is informed of the change.",
        "insertRootUndo": "This can be undone as long as nobody is assigned to the new unit and nothing has been shared with or forwarded to it.",
        "rootNameIsTenantName": "The name of the top-level unit is also the name of the tenant.",
        "unitDescriptionInternal": "Description (internal note)",
        "reparentNamed": "Move “{{unitName}}”",
        "wizardSteps": "Steps",
        "wizardStepName": "Name",
        "wizardStepPreview": "Preview",
        "wizardStepAdmin": "Administration",
        "insertRootConfirm": "Insert unit",
        "promotionsRevokedAt_one": "1 project forwarding is revoked (previously at {{units}}).",
        "promotionsRevokedAt_other": "{{count}} project forwardings are revoked (previously at {{units}})."
    },
    "governance": {
        "topicsVisibleFor": "Visible to: {{list}}",
        "topicsWhere": "Cross-cutting topics are assigned by the portfolio management of the originating unit in the “Categories” field.",
        "stepperTitle": "Key Project Promotion Pipeline",
        "stepperSubtitle": "Step-by-step delegation to parent portfolios without automatic escalation",
        "stepperAriaLabel": "Hierarchy approval path",
        "promotedSuccessAnnounce": "Project was promoted to {{unitName}}.",
        "revokedSuccessAnnounce": "Promotion for {{unitName}} was revoked.",
        "promotedByOn": "Promoted by {{name}} on {{date}}",
        "promoteAction": "Promote",
        "revokeAction": "Revoke Promotion",
        "promoteModalTitle": "Promote Project to Higher Tier",
        "promoteModalSubtitle": "Promotion from \"{{from}}\" to \"{{to}}\"",
        "promotionCommentLabel": "Justification / Note for the parent portfolio (optional)",
        "promotionCommentPlaceholder": "e.g. Meets key criteria for departmental focus.",
        "confirmPromote": "Promote Now",
        "levelEvaluationTitle": "Strategic Evaluation: {{unitName}}",
        "levelEvaluationSubtitle": "Tier-specific strategic prioritization for this portfolio",
        "executiveVaultBadge": "Executive Vault (Confidential)",
        "originLevelScoringHint": "On this origin tier, strategic prioritization is governed by the project's primary assessment above.",
        "strategicImportanceLabel": "Strategic Importance at this level (1–10)",
        "benefitLabel": "Expected Benefit at this level (1–10)",
        "portfolioNotesLabel": "Confidential Note / Briefing for this leadership committee",
        "portfolioNotesHelp": "Visible only to members of this organizational level. Hidden from project team members.",
        "portfolioNotesPlaceholder": "e.g. Agenda item for executive council: Additional credit required.",
        "evaluationSavedAnnounce": "Level evaluation saved successfully.",
        "promoteError": "Error during project promotion.",
        "revokeError": "Error revoking promotion.",
        "evaluateError": "Error saving evaluation.",
        "promotedAnnounce": "Project forwarded to {{unit}}.",
        "revokedAnnounce": "Forwarding withdrawn.",
        "actionError": "Action failed.",
        "origin": "Origin",
        "promotedAtBy": "{{date}} · {{name}}",
        "title": "Forwarding & cross-cutting topics",
        "subtitle": "Parent units and cross-cutting offices see the project with its risks, but without measures, and do not edit it.",
        "chain": "Forwarding chain",
        "notPromoted": "not forwarded",
        "promoteTo": "Forward to {{unit}}",
        "revoke": "Withdraw forwarding",
        "topicsTitle": "Cross-cutting topics",
        "promoteExplain": "{{unit}} will then see the project with its project data and risks (without measures) and can evaluate it or forward it further.",
        "comment": "Justification (optional)",
        "revokeExplain": "Your forwarding and all forwardings at higher levels will be withdrawn. The affected units will then no longer see the project.",
        "confirm": "Confirm"
    },
    "rollup": {
        "dashboardTitle": "CAF Rollup & Consolidation: {{unitName}}",
        "dashboardSubtitle": "Aggregated evaluation across {{count}} self-assessments from {{units}} organizational units",
        "weightedFteToggle": "FTE-Weighted Average",
        "viewModeLabel": "Switch View",
        "chartView": "Radar Chart",
        "tableView": "Table (Accessible)",
        "loadedAnnounce": "Rollup data updated successfully.",
        "noAssessmentsFound": "No published assessments found in the subtree of this organizational unit.",
        "radarChartTitle": "Consolidated Criteria Profile (CAF Maturity)",
        "rollupAverage": "Consolidated Average",
        "averageScore": "Average",
        "insightsTitle": "Cross-Cutting Insights",
        "insightsSubtitle": "Key patterns, strengths and variance across all subordinate units",
        "topStrengthBadge": "Top Strength",
        "topPotentialBadge": "Key Improvement Opportunity",
        "totalRemarksCount": "{{count}} qualitative findings (strengths & potentials) aggregated",
        "tableCaption": "CAF Rollup Metrics by Sub-Criteria",
        "subCriterionCol": "Sub-Criterion",
        "averageCol": "Average",
        "minCol": "Min",
        "maxCol": "Max",
        "varianceCol": "Spread (Δ)",
        "assessmentsCountCol": "Count",
        "childUnits": "subordinate units",
        "titleV2": "{{unit}}: aggregate of {{children}}",
        "explain": "For each unit, the latest assessment of the year that has been completed and shared with your unit counts; if there is none, the most recent earlier one. Each unit carries equal weight; sub-criteria without a score are not counted.",
        "year": "Year",
        "coverage": "Coverage: {{included}} of {{total}} {{children}}",
        "unitsCaption": "Units included",
        "unitCol": "Unit",
        "assessmentCol": "Assessment",
        "totalCol": "Overall score",
        "carriedOver": "carried over from {{year}}",
        "notIncluded": "not included (no shared assessment)",
        "noChildUnits": "This unit has no subordinate units.",
        "radarTitle": "Average per criterion",
        "totalMean": "Total {{value}} %",
        "average": "Average",
        "insights": "Notable findings",
        "strongest": "Highest average",
        "weakest": "Lowest average",
        "trend": "Overall score over time",
        "total": "Total",
        "subCriteriaTitle": "Sub-criteria",
        "spreadCol": "Spread (σ)",
        "nCol": "Units",
        "remarksTitle": "Remarks from the units",
        "remarkFilter": "Type of remark",
        "potentials": "Potentials",
        "strengths": "Strengths",
        "allRemarks": "All",
        "noRemarks": "No remarks.",
        "noAssessmentsFoundV2": "No shared assessments from {{children}} are available for this year."
    },
    "unitView": {
        "statuses": { "TOP_PRIORITY": "Top priority", "ACTIVE": "Active", "WATCHLIST": "Watchlist", "DEPRIORITIZED": "Deprioritized" },
        "categories": "Own categories",
        "savedAnnounce": "Evaluation saved.",
        "title": "{{unit}} view",
        "confidential": "Confidential: visible to your unit only. The originating unit and other levels cannot see this information.",
        "rank": "Rank",
        "status": "Portfolio status",
        "statusNone": "No status",
        "notes": "Confidential notes",
        "lastUpdate": "Last changed {{date}} {{name}}",
        "saved": "Saved.",
        "error": "Saving failed.",
        "statusUnknown": "No longer defined",
        "notRated": "Not rated",
        "confidentialOwn": "Confidential: visible only to portfolio management and management of your unit. Higher-level units do not see this information.",
        "rankPlaceholder": "e.g. 1",
        "saveView": "Save view",
        "newNote": "New note",
        "notePlaceholder": "New note for your unit …",
        "addNote": "Add note",
        "noteAddedAnnounce": "Note saved.",
        "confirmDeleteNote": "Really delete this note?",
        "noteDeletedAnnounce": "Note deleted.",
        "deleteNote": "Delete note",
        "loadingNotes": "Loading notes …",
        "noNotes": "No notes yet.",
        "unknownAuthor": "Unknown",
        "legacyNote": "earlier note"
    },
    "actions": {
        "reset": "Reset"
    },
    "assessmentLifecycle": {
        "statusFinal": "Completed",
        "statusSubmitted": "Submitted (external)",
        "statusDraft": "Draft",
        "releasedByChild": "Shared by {{unit}}",
        "releasedTo": "Shared with {{unit}}",
        "error": "Action failed.",
        "unknownUnit": "Organizational unit",
        "confidential": "Confidential (own unit only)",
        "finalize": "Complete",
        "finalizedAnnounce": "Assessment completed.",
        "release": "Share with {{unit}}",
        "releasedAnnounce": "Assessment shared.",
        "withdraw": "Withdraw sharing",
        "withdrawnAnnounce": "Sharing withdrawn.",
        "reopen": "Reopen",
        "reopenedAnnounce": "Assessment reopened.",
        "hintNew": "New assessments are drafts and are visible only within your unit.",
        "hintDraft": "A completed assessment can no longer be changed and can be shared with the parent unit.",
        "hintFinal": "Once shared, {{unit}} sees the complete assessment, including remarks. Higher levels do not see it individually; it flows anonymously into the overall average.",
        "hintForeign": "Shared by a subordinate unit. Read-only.",
        "saveFirst": "Please save first, then complete."
    },
    "comparison": {
        "title": "Comparison with other units",
        "intro": "Averages only, as of {{year}}. Every unit counts equally.",
        "directColumn": "Units directly under {{group}}",
        "overallColumn": "All units under {{root}}",
        "explainDirect": "Units directly under {{group}}: including your unit, if shared; shown from {{min}} units onwards. Based on {{count}} of {{total}} units.",
        "explainDirectTop": "Units directly under {{group}}: your own assessment is not included. Based on {{count}} of {{total}} units.",
        "explainOverall": "All units under {{root}}: across all levels, excluding {{root}} itself, in whole percent. Based on {{count}} of {{total}} units.",
        "tooFew": "Too few units directly under {{group}} for a comparison (at least {{min}}).",
        "noReleases": "No shared assessments yet from units directly under {{group}}.",
        "overallTooFew": "Not enough shared assessments yet for an overall average. The comparison is limited to the direct units.",
        "caption": "Own scores compared with the averages",
        "criterion": "Criterion",
        "own": "Own unit",
        "difference": "Difference",
        "total": "Total"
    },
    "topics": {
        "validationRequired": "Name and responsible unit are required.",
        "savedAnnounce": "Cross-cutting topic saved.",
        "saveError": "Saving failed.",
        "confirmDelete": "Delete cross-cutting topic \"{{name}}\"? It will be removed from all projects and the responsible unit will lose access.",
        "deletedAnnounce": "Cross-cutting topic deleted.",
        "deleteError": "Deletion failed.",
        "title": "Cross-cutting topics",
        "intro": "Categories across the hierarchy with a responsible unit, e.g. IT or sustainability. They appear as a category in all units. When portfolio management assigns a project to a cross-cutting topic, it becomes visible to the responsible unit (project data and risks, without measures).",
        "add": "Create topic",
        "empty": "No cross-cutting topics defined yet.",
        "owner": "Responsible",
        "inactive": "deactivated",
        "editNamed": "Edit {{name}}",
        "activate": "Activate",
        "deactivate": "Deactivate",
        "deleteNamed": "Delete {{name}}",
        "edit": "Edit topic",
        "name": "Name",
        "ownerUnit": "Responsible unit",
        "ownerChangeHint": "Access to all projects of this topic passes to the new unit.",
        "description": "Description",
        "color": "Color"
    },
    "unitCategories": {
        "sectionTitle": "Categories: {{unit}}",
        "intro": "Own categories are visible only to your unit and are used for organizing, filtering and roadmaps; they do not share anything.",
        "error": "Action failed.",
        "empty": "No own categories yet.",
        "addedAnnounce": "Category created.",
        "updatedAnnounce": "Category saved.",
        "deletedAnnounce": "Category deleted.",
        "colorFor": "Color of {{name}}",
        "nameFor": "Name of {{name}}",
        "confirmDelete": "Delete category “{{name}}”? It will also be removed from the projects.",
        "deleteNamed": "Delete {{name}}",
        "newColor": "Color of the new category",
        "newName": "Name of the new category",
        "placeholder": "e.g. Legislative goal 3",
        "add": "Add",
        "topicsTitle": "Cross-cutting topics",
        "topicsIntro": "Defined by the tenant and available as a category in all units. Assigning one makes a project visible to the responsible unit.",
        "topicOwner": "responsible: {{unit}}"
    },
    "unitStatuses": {
        "sectionTitle": "Portfolio status: {{unit}}",
        "intro": "Your unit uses these statuses to classify projects in its confidential view. Other units do not see them.",
        "customHint": "Your unit uses its own statuses.",
        "defaultHint": "The defaults currently apply; saving changes creates your own list.",
        "nameFor": "Name of status {{position}}",
        "moveUp": "Move {{name}} up",
        "moveDown": "Move {{name}} down",
        "remove": "Remove {{name}}",
        "newName": "Name of the new status",
        "placeholder": "e.g. For discussion by the executive board",
        "add": "Add",
        "reset": "Use defaults",
        "discard": "Discard",
        "save": "Save statuses",
        "savedAnnounce": "Statuses saved.",
        "savedCleared_one": "Statuses saved. 1 project no longer has a status.",
        "savedCleared_other": "Statuses saved. {{count}} projects no longer have a status.",
        "error": "Saving failed.",
        "nameMissing": "Every status needs a name.",
        "confirmRemove": "Removed statuses ({{list}}) will be cleared from your unit’s projects. Continue?",
        "confirmReset": "Use the default statuses again? Projects with one of your own statuses will lose it."
    },
    "sharingBadge": {
        "receivedFrom": "Received from {{unit}}",
        "receivedTopic": "Received via the cross-cutting topic {{topic}}",
        "promotedTo": "Passed on to {{unit}}",
        "sharedTopic": "Shared with {{unit}} (cross-cutting topic {{topic}})"
    },
    "topicShare": {
        "title": "Confirm sharing with cross-cutting offices",
        "gainAccess": "Gain insight into this project (project data and risks, without measures):",
        "loseAccess": "Lose insight:",
        "confirm": "Confirm and save"
    }
};
